import { useEffect, useState, useCallback } from "react";
import { InfoHint } from "../components/InfoHint";
import { FiltriCollapsible } from "../components/FiltriCollapsible";
import { api, formatApiError, openSecureFile } from "../lib/api";
import { AuthImage } from "../components/AuthImage";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { SharedCard, getBandSinistro } from "../components/SharedCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { PhotoUploader } from "../components/PhotoUploader";
import { DocumentUploader } from "../components/DocumentUploader";
import { SinistroChat } from "../components/SinistroChat";
import { AttoLiquidazioneCard } from "../components/AttoLiquidazioneCard";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { ShieldAlert, Plus, Pencil, Trash2, Building2, FileText, Clock, Umbrella, Users, X, Send, Download, BellRing, UserCog, HardHat, Scale, CheckCircle2, AlertCircle, Camera, Folder, Euro, MessageCircle, ChevronDown, Wrench, ExternalLink, RotateCcw, Calendar } from "lucide-react";

// Iter122k76 · Fasi 3 e 4 unite in un'unica fase "Valutazione/Liquidazione".
// Le pratiche legacy salvate come "Aperto-Liquidazione" sono trattate come
// "Aperto-Valutazione" a livello di stepper/display (mapping `phaseForDisplay`).
// Il backend continua ad accettare tutte e 5 le fasi per compatibilità.
const FASI = ["Bozza-Denuncia", "Aperto-Istruttoria", "Aperto-Valutazione", "Chiuso-Liquidato"];
// Iter122cs · retro-compat: mostra label "amichevole" delle fasi
const FASE_LABEL = {
  "Bozza-Denuncia": "Bozza",
  "Aperto-Istruttoria": "Istruttoria",
  "Aperto-Valutazione": "Valutazione/Liquidazione",
  "Aperto-Liquidazione": "Valutazione/Liquidazione",
  "Chiuso-Liquidato": "Chiuso",
};
// Iter122k76 · mappa fase memorizzata → fase mostrata nello stepper (5→4).
const phaseForDisplay = (f) => (f === "Aperto-Liquidazione" ? "Aperto-Valutazione" : f);
const FASE_STYLE = {
  "Bozza-Denuncia": "bg-slate-100 text-slate-700 border-slate-400",
  "Aperto-Istruttoria": "bg-yellow-100 text-yellow-900 border-yellow-400",
  "Aperto-Valutazione": "bg-orange-100 text-orange-800 border-orange-400",
  "Aperto-Liquidazione": "bg-orange-100 text-orange-800 border-orange-400",
  "Chiuso-Liquidato": "bg-green-100 text-green-800 border-green-300",
  // legacy fallback (record vecchi non ancora migrati in memoria)
  Aperto: "bg-slate-100 text-slate-700 border-slate-400",
  "In gestione": "bg-orange-100 text-orange-800 border-orange-400",
  Definito: "bg-green-100 text-green-800 border-green-300",
};

const EMPTY = {
  condominio_id: "", ticket_id: null, ticket_ids: [],
  // Iter123k172 · Amministratore legale rappresentante (recuperato dal condominio.referente)
  amministratore_legale: "",
  // Iter122fk · Iter122fv · nuovi campi Bozza
  oggetto: "", data_sinistro: "", descrizione: "", interventi_urgenti: false, interventi_descrizione: "", note_aggiuntive: "",
  fase: "Bozza-Denuncia",
  // Iter122fp22 · flag invio selettivo denuncia (default entrambi true → comportamento invariato)
  invia_a_compagnia: true, invia_a_agenzia: true,
  compagnia_fornitore_id: "", compagnia_nome: "", compagnia_contatti: "", compagnia_email: "", compagnia_pec: "",
  numero_polizza: "", polizza_scadenza: "",
  agenzia_fornitore_id: "", agenzia_nome: "", agenzia_contatti: "", agenzia_email: "", agenzia_pec: "",
  danneggiati: [], danneggianti: [],
  numero_sinistro: "", data_apertura: "",
  perito_fornitore_id: "", perito_nome: "", perito_contatti: "", perito_email: "",
  liquidatore_fornitore_id: "", liquidatore_nome: "", liquidatore_contatti: "", liquidatore_email: "",
  importo_richiesto: "", importo_liquidato: "", note: "",
  documentazione_richiesta: false, documentazione_scadenza: "",
  esito_valutazione: "", esito_motivazione: "", controdeduzioni_studio: "",
  pagamento_confermato: false, documenti_pratica: [],
  liquidazione_offerta: "", liquidazione_franchigia: "", liquidazione_riparto: [], liquidazione_note: "",
  // Iter122k95 · Atto di Liquidazione gestito via editor esterno (`/conferma-preventivo`)
  //   e collezione `db.conferme` con `sinistro_id`. Nessun campo template qui.
  messaggi: [],
};

// Iter122cu · parsing/formattazione importi in formato italiano
const parseEuro = (v) => {
  let s = String(v || "").replace("€", "").trim();
  if (!s) return 0;
  if (s.includes(",")) s = s.replace(/\./g, "").replace(",", ".");
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
};
const fmtEuro = (n) => n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

function FaseBadge({ fase }) {
  const cls = FASE_STYLE[fase] || FASE_STYLE["Bozza-Denuncia"];
  const label = FASE_LABEL[fase] || fase;
  return <span className={`px-2 py-0.5 text-xs font-bold uppercase tracking-wide rounded-full border ${cls}`}>{label}</span>;
}

export default function Sinistri() {
  const { user: authUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [condomini, setCondomini] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [assicuratori, setAssicuratori] = useState([]);
  const [periti, setPeriti] = useState([]);
  const [liquidatori, setLiquidatori] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState("");
  const [filterFase, setFilterFase] = useState("");
  // Iter122ik · ricerca testuale + filtro condominio
  const [filterCondominio, setFilterCondominio] = useState("");
  const [searchQ, setSearchQ] = useState("");
  // Iter122jd · Ordinamento: "recenti" (default, updated_at desc) | "denuncia" (data invio denuncia desc)
  const [sortBy, setSortBy] = useState("recenti");
  // Iter122k131 · Pannello ricerca & filtri richiudibile
  const [sinFiltriOpen, setSinFiltriOpen] = useState(false);
  const [accordi, setAccordi] = useState([]);
  // Iter122cs · Tabs SOGGETTI/GESTIONE
  const [mainTab, setMainTab] = useState("soggetti");       // "soggetti" | "gestione"
  // Iter122h2 · Sotto-categorie a scomparsa (accordion, tutte chiuse di default)
  const [openSoggetti, setOpenSoggetti] = useState(() => new Set());
  const [openGestione, setOpenGestione] = useState(() => new Set());
  const toggleSoggetti = (k) => setOpenSoggetti((prev) => {
    const next = new Set(prev); if (next.has(k)) next.delete(k); else next.add(k); return next;
  });
  const toggleGestione = (k) => setOpenGestione((prev) => {
    const next = new Set(prev); if (next.has(k)) next.delete(k); else next.add(k); return next;
  });
  const [transizioneWarnings, setTransizioneWarnings] = useState(null); // { fase, warnings }
  // Iter122ij · Flag read-only per fornitore Compagnia/Agenzia in Istruttoria (whitelist backend).
  const isFornitoreCompagnia = authUser?.role === "fornitore";
  const readOnlyMode = isFornitoreCompagnia; // fornitori possono editare SOLO i campi in whitelist
  const isEditableByForn = (field) => new Set([
    "numero_sinistro", "perito_fornitore_id", "perito_nome", "perito_contatti",
    "liquidatore_fornitore_id", "liquidatore_nome", "liquidatore_contatti",
  ]).has(field);
  const roFor = (field) => (readOnlyMode && !isEditableByForn(field));
  const roClass = (field) => roFor(field) ? "bg-slate-100 text-slate-500 cursor-not-allowed" : "";
  const loadAccordi = (sid) => { setAccordi([]); if (!sid) return; api.get("/conferme", { params: { sinistro_id: sid } }).then((r) => setAccordi(r.data.conferme || [])).catch(() => {}); };

  const load = useCallback(() => {
    // Iter122k153 · Filtro speciale "cestino": mostra i sinistri soft-deletati
    const isCestino = filterFase === "cestino";
    const params = isCestino
      ? { cestino: 1 }
      : (filterFase ? { fase: filterFase } : {});
    api.get("/sinistri", { params }).then((r) => setList(r.data)).catch(() => {});
  }, [filterFase]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/condomini").then((r) => setCondomini(r.data)).catch(() => {});
    api.get("/tickets").then((r) => setTickets(r.data)).catch(() => {});
    api.get("/fornitori", { params: { categoria: "Assicurazione", limit: 500 } })
      .then((r) => setAssicuratori(r.data.fornitori || [])).catch(() => {});
    api.get("/fornitori", { params: { categoria: "Perito", limit: 500 } })
      .then((r) => setPeriti(r.data.fornitori || [])).catch(() => {});
    api.get("/fornitori", { params: { categoria: "Liquidatore", limit: 500 } })
      .then((r) => setLiquidatori(r.data.fornitori || [])).catch(() => {});
  }, []);

  const openNew = useCallback((prefill = {}) => {
    setForm({ ...EMPTY, ...prefill }); setEditId(null); setError(""); setOpen(true);
  }, []);
  const openEdit = (s) => {
    setForm({ ...EMPTY, ...s });
    setEditId(s.id);
    setError("");
    setOpen(true);
    loadAccordi(s.id);
    // Iter122iz · marca come "visto" per resettare i badge push (messaggi + doc nuovi)
    if ((s.messaggi_non_letti || 0) + (s.doc_nuovi || 0) > 0) {
      api.post(`/sinistri/${s.id}/visto`).then(() => load()).catch(() => {});
    }
  };

  // Prefill from ticket detail navigation (apertura sinistro dalla sezione Manutenzioni)
  useEffect(() => {
    if (location.state?.ticket_id) {
      const tid = location.state.ticket_id;
      const cid = location.state.condominio_id || "";
      setForm({ ...EMPTY, ticket_id: tid, condominio_id: cid });
      setEditId(null); setError(""); setOpen(true);
      onTicketChange(tid);
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Iter122fu · Rubrica soggetti del condominio del sinistro — per popolare in automatico
  // i campi Danneggiato/i e Danneggiante/i (nome, contatti) via picker.
  const [contattiCond, setContattiCond] = useState([]);
  useEffect(() => {
    if (!open || !form.condominio_id) { setContattiCond([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/condomini/${form.condominio_id}/contatti`);
        if (!cancelled) setContattiCond(r.data || []);
      } catch { if (!cancelled) setContattiCond([]); }
    })();
    return () => { cancelled = true; };
  }, [open, form.condominio_id]);
  // Iter122fp13 · Aggiunge un danneggiato/danneggiante partendo dalla rubrica del condominio,
  // popolando nome/telefono/email/residenza in un solo click. Registra `_rubrica_id` sull'item
  // per il chip strip (dedupe + mapping X→rimuovi).
  const addFromRubrica = (kind, sid) => {
    if (!sid) return;
    const s = contattiCond.find((c) => (c.id || c.soggetto_id) === sid);
    if (!s) return;
    const item = {
      nome: s.nome || s.ragione_sociale || "",
      contatti: s.telefono || s.phone || "",
      email: s.email || "",
      residenza: s.residenza || "",
      descrizione: "", foto: [], allegati: [],
      _rubrica_id: sid,
    };
    if (kind === "danneggiato") {
      setForm((f) => ({ ...f, danneggiati: [...(f.danneggiati || []), item] }));
    } else {
      setForm((f) => ({ ...f, danneggianti: [...(f.danneggianti || []), item] }));
    }
  };

  const pickSoggetto = (kind, i, sid) => {
    if (!sid) return;
    const s = contattiCond.find((c) => (c.id || c.soggetto_id) === sid);
    if (!s) return;
    const nome = s.nome || s.ragione_sociale || "";
    const contatti = s.telefono || s.phone || "";
    const email = s.email || "";
    const residenza = s.residenza || "";
    // Iter122fp9 · Merge non-distruttivo: sovrascrivi solo i campi vuoti dell'utente
    const merge = (row) => ({
      ...row,
      nome: row.nome && row.nome.trim() ? row.nome : nome,
      contatti: row.contatti && row.contatti.trim() ? row.contatti : contatti,
      email: row.email && row.email.trim() ? row.email : email,
      residenza: row.residenza && row.residenza.trim() ? row.residenza : residenza,
    });
    if (kind === "danneggiato") {
      setForm((f) => { const d = [...f.danneggiati]; d[i] = merge(d[i] || {}); return { ...f, danneggiati: d }; });
    } else {
      setForm((f) => { const d = [...(f.danneggianti || [])]; d[i] = merge(d[i] || {}); return { ...f, danneggianti: d }; });
    }
  };

  // Iter122ct · Non-destructive auto-fill dei dati assicurativi del condominio (nuova pratica).
  // Copre i casi in cui il condominio è preselezionato all'apertura del dialog
  // (da manutenzione, da URL state, da openNew con prefill) e la selezione
  // avviene prima che `condomini` sia caricato via API.
  useEffect(() => {
    if (!open || editId) return;                       // solo nuove pratiche
    if (!form.condominio_id) return;
    const c = condomini.find((x) => x.id === form.condominio_id);
    if (!c) return;
    // Iter123k172 · Precompila l'Amministratore legale rapp.te dal condominio se non ancora impostato
    if (c.referente && !form.amministratore_legale) {
      setForm((f) => ({ ...f, amministratore_legale: c.referente }));
    }
    const hasIns = c.compagnia_nome || c.agenzia_nome || c.numero_polizza;
    if (!hasIns) return;
    setForm((f) => {
      // Salta se l'utente ha già dati manualmente (evita di sovrascrivere)
      if (f.compagnia_nome || f.agenzia_nome) return f;
      return {
        ...f,
        compagnia_fornitore_id: c.compagnia_fornitore_id || f.compagnia_fornitore_id,
        compagnia_nome: c.compagnia_nome || f.compagnia_nome,
        compagnia_contatti: c.compagnia_contatti || f.compagnia_contatti,
        compagnia_email: c.compagnia_email || f.compagnia_email,
        compagnia_pec: c.compagnia_pec || f.compagnia_pec,
        numero_polizza: c.numero_polizza || f.numero_polizza,
        polizza_scadenza: c.polizza_scadenza || f.polizza_scadenza,
        agenzia_fornitore_id: c.agenzia_fornitore_id || f.agenzia_fornitore_id,
        agenzia_nome: c.agenzia_nome || f.agenzia_nome,
        agenzia_contatti: c.agenzia_contatti || f.agenzia_contatti,
        agenzia_email: c.agenzia_email || f.agenzia_email,
        agenzia_pec: c.agenzia_pec || f.agenzia_pec,
      };
    });
  }, [open, editId, form.condominio_id, condomini]);

  const onCondominioChange = (cid) => {
    const c = condomini.find((x) => x.id === cid);
    const hasIns = c && (c.compagnia_nome || c.agenzia_nome || c.numero_polizza);
    setForm((f) => {
      const base = {
        ...f,
        condominio_id: cid,
        // Iter123k172 · precompila l'Amministratore legale rapp.te dal condominio
        amministratore_legale: (c && c.referente) || f.amministratore_legale || "",
      };
      if (!hasIns) return base;
      return {
        ...base,
        compagnia_fornitore_id: c.compagnia_fornitore_id || base.compagnia_fornitore_id,
        compagnia_nome: c.compagnia_nome || base.compagnia_nome,
        compagnia_contatti: c.compagnia_contatti || base.compagnia_contatti,
        compagnia_email: c.compagnia_email || base.compagnia_email,
        compagnia_pec: c.compagnia_pec || base.compagnia_pec,
        numero_polizza: c.numero_polizza || base.numero_polizza,
        polizza_scadenza: c.polizza_scadenza || base.polizza_scadenza,
        agenzia_fornitore_id: c.agenzia_fornitore_id || base.agenzia_fornitore_id,
        agenzia_nome: c.agenzia_nome || base.agenzia_nome,
        agenzia_contatti: c.agenzia_contatti || base.agenzia_contatti,
        agenzia_email: c.agenzia_email || base.agenzia_email,
        agenzia_pec: c.agenzia_pec || base.agenzia_pec,
      };
    });
    if (hasIns) toast.success("Dati assicurativi precompilati dal condominio.");
  };

  const onTicketChange = async (tid) => {
    set("ticket_id", tid || null);
    if (!tid) return;
    try {
      const { data: t } = await api.get(`/tickets/${tid}`);
      setForm((f) => {
        const danneggiati = (t.contatti_referenti || []).map((c) => ({
          nome: c.nome, contatti: c.telefono || "", descrizione: "", foto: [], allegati: [],
        }));
        if (danneggiati.length === 0) {
          danneggiati.push({ nome: t.condomino_nome || "Danneggiato", contatti: t.telefono_contatto || "", descrizione: "", foto: [], allegati: [] });
        }
        const foto = (t.foto || []).map((p) => ({ id: p.id, filename: p.original_filename || "foto" }));
        const allegati = (t.allegati || []).map((a) => ({ id: a.id, filename: a.original_filename || "documento" }));
        danneggiati[0].foto = [...danneggiati[0].foto, ...foto];
        danneggiati[0].allegati = [...danneggiati[0].allegati, ...allegati];
        return {
          ...f, ticket_id: tid,
          condominio_id: f.condominio_id || t.condominio_id || "",
          descrizione: t.descrizione || f.descrizione || "",
          danneggiati,
        };
      });
      toast.success("Dati precompilati dalla manutenzione collegata.");
    } catch { /* noop */ }
  };

  const selectAssicuratore = (kind, fid) => {
    const fo = assicuratori.find((a) => a.id === fid);
    setForm((f) => ({
      ...f,
      [`${kind}_fornitore_id`]: fid,
      [`${kind}_nome`]: fo ? fo.ragione_sociale : f[`${kind}_nome`],
      [`${kind}_email`]: fo ? (fo.email || "") : f[`${kind}_email`],
      [`${kind}_contatti`]: fo ? (fo.telefono || fo.referente || "") : f[`${kind}_contatti`],
    }));
  };

  // Iter122cs · selezione Perito/Liquidatore dai fornitori dedicati
  const selectRuolo = (kind, list, fid) => {
    const fo = list.find((a) => a.id === fid);
    setForm((f) => ({
      ...f,
      [`${kind}_fornitore_id`]: fid,
      [`${kind}_nome`]: fo ? fo.ragione_sociale : f[`${kind}_nome`],
      [`${kind}_email`]: fo ? (fo.email || "") : f[`${kind}_email`],
      [`${kind}_contatti`]: fo ? (fo.telefono || fo.referente || "") : f[`${kind}_contatti`],
    }));
  };

  // Iter122cs · Avanza fase con checklist di ingresso
  // Iter122fp12 · Pre-check locale bloccante per uscire dalla Bozza + gestione errors dal backend
  const avanzaFase = async (nuovaFase, force = false) => {
    if (!editId) {
      toast.error("Salva prima la pratica.");
      return;
    }
    // Pre-check locale se stiamo uscendo dalla Bozza (evita round-trip inutile)
    if (nuovaFase !== "Bozza-Denuncia") {
      const missing = campiObbligatoriMancanti(form);
      if (missing.length) {
        toast.error("Per far avanzare la pratica compila prima i campi obbligatori (asterisco rosso): " + missing.join(", "));
        return;
      }
    }
    try {
      // Salva stato corrente prima di avanzare (così i warning valutano lo stato aggiornato)
      await api.put(`/sinistri/${editId}`, form);
      const { data } = await api.post(`/sinistri/${editId}/avanza-fase`, { fase: nuovaFase, force });
      // Iter122fp12 · errori bloccanti dal backend (mai bypassabili)
      if ((data.errors || []).length) {
        toast.error("Campi obbligatori mancanti: " + data.errors.join(", "));
        return;
      }
      if (data.applied) {
        toast.success(`Fase aggiornata: ${FASE_LABEL[data.fase_attuale] || data.fase_attuale}`);
        setForm((f) => ({ ...f, fase: data.fase_attuale }));
        setTransizioneWarnings(null);
        load();
      } else {
        // Mostra warning e chiedi conferma
        setTransizioneWarnings({ fase: data.fase_richiesta, warnings: data.warnings });
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const addDanneggiato = () => setForm((f) => ({ ...f, danneggiati: [...(f.danneggiati || []), { nome: "", contatti: "", email: "", residenza: "", descrizione: "", foto: [], allegati: [] }] }));
  const updateDanneggiato = (i, k, v) => setForm((f) => { const d = [...f.danneggiati]; d[i] = { ...d[i], [k]: v }; return { ...f, danneggiati: d }; });
  const removeDanneggiato = (i) => setForm((f) => ({ ...f, danneggiati: f.danneggiati.filter((_, idx) => idx !== i) }));
  // Iter122fk · Danneggianti (soggetti terzi presunti responsabili del sinistro)
  const addDanneggiante = () => setForm((f) => ({ ...f, danneggianti: [...(f.danneggianti || []), { nome: "", contatti: "", email: "", residenza: "", descrizione: "", foto: [], allegati: [] }] }));
  const updateDanneggiante = (i, k, v) => setForm((f) => { const d = [...(f.danneggianti || [])]; d[i] = { ...d[i], [k]: v }; return { ...f, danneggianti: d }; });
  const removeDanneggiante = (i) => setForm((f) => ({ ...f, danneggianti: (f.danneggianti || []).filter((_, idx) => idx !== i) }));

  // Iter122cu · Fase 4 · riparto liquidazione
  const addRipartoRow = () => setForm((f) => ({ ...f, liquidazione_riparto: [...(f.liquidazione_riparto || []), { nome: "", importo: "", note: "" }] }));
  const updateRiparto = (i, k, v) => setForm((f) => { const r = [...(f.liquidazione_riparto || [])]; r[i] = { ...r[i], [k]: v }; return { ...f, liquidazione_riparto: r }; });
  const removeRiparto = (i) => setForm((f) => ({ ...f, liquidazione_riparto: (f.liquidazione_riparto || []).filter((_, idx) => idx !== i) }));
  const prefillRipartoDanneggiati = () => setForm((f) => ({
    ...f,
    liquidazione_riparto: (f.danneggiati || []).filter((d) => d.nome).map((d) => ({ nome: d.nome, importo: "", note: "" })),
  }));

  // Iter122cu · Fase 5 · report finale PDF
  const downloadReport = async () => {
    try {
      await api.put(`/sinistri/${editId}`, form);
      const res = await api.get(`/sinistri/${editId}/report/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `report_sinistro_${form.numero_pratica || editId}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  // Iter122k81 · Invio INTEGRAZIONE denuncia (soggetti ritardatari / foto aggiunti)
  //   a Perito e/o Agenzia. Attivo solo dopo la trasmissione della denuncia originaria.
  const inviaIntegrazione = async () => {
    if (!editId) return;
    if (!(form.perito_email || "").trim() && !(form.agenzia_email || "").trim()) {
      toast.error("Inserisci almeno l'email del Perito o dell'Agenzia per inviare l'integrazione.");
      return;
    }
    if (!window.confirm(`Confermi l'invio dell'INTEGRAZIONE denuncia al Perito e all'Agenzia?\n\n` +
                         `Verrà trasmesso il dossier completo aggiornato (dati soggetti + foto/documenti), ` +
                         `evidenziando i nuovi soggetti danneggiati/danneggianti aggiunti dopo la denuncia originaria.`)) return;
    try {
      await api.put(`/sinistri/${editId}`, form);
      const { data } = await api.post(`/sinistri/${editId}/integrazione`);
      const dest = (data.destinatari || []).map((d) => `${d.nome}${d.sent ? "" : " (errore)"}`).join(", ");
      const falliti = (data.destinatari || []).filter((d) => !d.sent);
      if (falliti.length) {
        const detail = falliti.map((d) => `${d.nome}: ${d.error || "errore invio"}`).join("\n");
        toast.error(`Invio non riuscito per ${falliti.length}/${data.destinatari.length} destinatari.\n${detail}`, { duration: 15000 });
        return;
      }
      const extra = [];
      if (data.nuovi_danneggiati) extra.push(`${data.nuovi_danneggiati} nuovo/i danneggiato/i`);
      if (data.nuovi_danneggianti) extra.push(`${data.nuovi_danneggianti} nuovo/i danneggiante/i`);
      toast.success(`Integrazione trasmessa a: ${dest} — ${data.allegati} allegati${extra.length ? ` · ${extra.join(", ")}` : ""}.`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const trasmettiDenuncia = async () => {
    // Iter122fp12 · Pre-check locale sui campi obbligatori (bloccante) — evita round-trip inutile
    const missing = campiObbligatoriMancanti(form);
    if (missing.length) {
      toast.error("Compila prima i campi obbligatori (asterisco rosso): " + missing.join(", "));
      return;
    }
    try {
      await api.put(`/sinistri/${editId}`, form);
      const { data } = await api.post(`/sinistri/${editId}/denuncia`);
      const dest = (data.destinatari || []).map((d) => `${d.nome}${d.sent ? "" : " (errore)"}`).join(", ");
      // Iter122il · Se qualche invio è fallito, mostra un toast di errore con la causa reale
      const falliti = (data.destinatari || []).filter((d) => !d.sent);
      if (falliti.length) {
        const detail = falliti.map((d) => `${d.nome}: ${d.error || "errore invio"}`).join("\n");
        toast.error(`Invio non riuscito per ${falliti.length}/${data.destinatari.length} destinatari.\n${detail}`, { duration: 15000 });
      }
      // Iter122h3 · aggiorna localmente la fase se il backend l'ha avanzata a Istruttoria
      if (data.fase_avanzata && data.fase_attuale) {
        setForm((f) => ({ ...f, fase: data.fase_attuale }));
        if (!falliti.length) toast.success(`Denuncia trasmessa a: ${dest} — ${data.allegati} allegati. Fase avanzata a Istruttoria.`);
      } else if (!falliti.length) {
        toast.success(`Denuncia trasmessa a: ${dest} — ${data.allegati} allegati.`);
      }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const downloadDenuncia = async () => {
    try {
      const res = await api.get(`/sinistri/${editId}/denuncia/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `denuncia_${form.numero_pratica || editId}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  // Iter122k40 · Invio "Scheda sinistro" al perito
  const inviaSchedaPerito = async () => {
    if (!editId) return;
    if (!(form.perito_email || "").trim()) {
      toast.error("Inserisci l'email del perito prima di inviare la scheda.");
      return;
    }
    try {
      await api.put(`/sinistri/${editId}`, form);
      const { data } = await api.post(`/sinistri/${editId}/invia-scheda-perito`);
      if (!data.ok) {
        toast.error(`Invio non riuscito: ${data.error || "errore invio"}`, { duration: 12000 });
        return;
      }
      const bcc = data.copia_studio && data.copia_studio.sent
        ? ` · BCC allo Studio (${data.copia_studio.email})` : "";
      toast.success(`Scheda sinistro inviata a ${data.destinatario.email} — ${data.allegati} allegati${bcc}.`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const sollecitaDoc = async () => {    try {
      await api.put(`/sinistri/${editId}`, form);
      const { data } = await api.post(`/sinistri/${editId}/sollecita-doc`);
      // Iter122k113 · Nessun destinatario valido → warning (fallback legacy rimosso).
      if ((data.destinatari || 0) === 0) {
        toast.warning(
          "Sollecito NON inviato: nessun danneggiato/danneggiante ha email o account app collegato. Aggiungi un'email o collega un account e riprova.",
          { duration: 12000 },
        );
      } else {
        toast.success(`Sollecito documentazione inviato a ${data.destinatari} destinatario/i.`);
      }
      setForm((f) => ({ ...f, documentazione_richiesta: true })); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const save = async () => {
    setError("");
    try {
      if (editId) await api.put(`/sinistri/${editId}`, form);
      else await api.post("/sinistri", form);
      setOpen(false);
      load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    // Iter122k153 · soft-delete: sposta nel cestino
    if (!window.confirm("Spostare la pratica nel cestino? Potrai ripristinarla dal filtro Cestino.")) return;
    try {
      await api.delete(`/sinistri/${id}`);
      toast.success("Pratica spostata nel cestino.");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const ripristina = async (id) => {
    try {
      await api.post(`/sinistri/${id}/ripristina`);
      toast.success("Pratica ripristinata.");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const rimuoviDefinitivo = async (id, numero) => {
    if (!window.confirm(`Eliminare DEFINITIVAMENTE la pratica ${numero || ""}? L'operazione NON è reversibile.`)) return;
    try {
      await api.delete(`/sinistri/${id}/definitivo`);
      toast.success("Pratica eliminata definitivamente.");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-6xl">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-3">
            <ShieldAlert className="w-9 h-9 text-amber-600" /> Sinistri assicurativi
          </h1>
          <InfoHint section="sinistri" />
        </div>
        <Button data-testid="add-sinistro-button" onClick={() => openNew()} className="h-12 px-5 text-base font-bold bg-brand hover:bg-brand-dark">
          <Plus className="w-5 h-5 mr-2" /> Nuova pratica
        </Button>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-3">
        <Button data-testid="fase-filter-all" variant={filterFase === "" ? "default" : "outline"} className="h-8 px-2.5 text-xs sm:text-sm" onClick={() => setFilterFase("")}>Tutte</Button>
        {FASI.map((f) => (
          <Button key={f} data-testid={`fase-filter-${f}`} variant={filterFase === f ? "default" : "outline"} className="h-8 px-2.5 text-xs sm:text-sm" onClick={() => setFilterFase(f)}>{FASE_LABEL[f]}</Button>
        ))}
        {/* Iter122k153 · Filtro Cestino (soft-deleted): stile rosso per distinguere. */}
        <Button data-testid="fase-filter-cestino"
          variant={filterFase === "cestino" ? "default" : "outline"}
          className={`h-8 px-2.5 text-xs sm:text-sm ${filterFase === "cestino"
            ? "bg-red-600 hover:bg-red-700 text-white"
            : "border-red-300 text-red-700 hover:bg-red-50"}`}
          onClick={() => setFilterFase("cestino")}>
          <Trash2 className="w-3.5 h-3.5 mr-1" /> Cestino
        </Button>
      </div>

      {/* Iter122ik · Barra ricerca + filtro condominio (lato client) */}
      {/* Iter122jd · + Ordinamento (recenti / data denuncia) */}
      {/* Iter122k131 · Pannello ricerca & filtri richiudibile */}
      <FiltriCollapsible
        open={sinFiltriOpen} setOpen={setSinFiltriOpen}
        count={(searchQ ? 1 : 0) + (filterCondominio ? 1 : 0) + (sortBy !== "recenti" ? 1 : 0)}
        onReset={() => { setSearchQ(""); setFilterCondominio(""); setSortBy("recenti"); }}
        testidPrefix="sinistri">
        <div className="grid sm:grid-cols-[1fr_220px_200px] gap-2">
          <Input data-testid="sinistro-search" placeholder="Cerca per numero pratica, oggetto, descrizione, compagnia, n° sinistro…"
            value={searchQ} onChange={(e) => setSearchQ(e.target.value)} className="h-10" />
          <select data-testid="sinistro-filter-condominio"
            value={filterCondominio} onChange={(e) => setFilterCondominio(e.target.value)}
            className="h-10 border-2 border-slate-200 rounded-lg px-2 bg-white text-sm">
            <option value="">Tutti i condomini</option>
            {condomini.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
          <select data-testid="sinistro-sort"
            value={sortBy} onChange={(e) => setSortBy(e.target.value)}
            className="h-10 border-2 border-slate-200 rounded-lg px-2 bg-white text-sm">
            <option value="recenti">Ordina: aggiornati di recente</option>
            <option value="denuncia">Ordina: data denuncia (più recenti)</option>
          </select>
        </div>
      </FiltriCollapsible>

      <div className="mb-3" />

      <div className="space-y-2">
        {(() => {
          const q = searchQ.trim().toLowerCase();
          const visible = list.filter((s) => {
            if (filterCondominio && s.condominio_id !== filterCondominio) return false;
            if (!q) return true;
            const hay = [s.numero_pratica, s.oggetto, s.descrizione, s.condominio_nome,
                         s.compagnia_nome, s.agenzia_nome, s.numero_sinistro].join(" ").toLowerCase();
            return hay.includes(q);
          });
          // Iter122jd · Ordinamento: per data denuncia (i sinistri SENZA denuncia vanno in fondo)
          if (sortBy === "denuncia") {
            visible.sort((a, b) => {
              const da = a.denuncia_trasmessa_at ? new Date(a.denuncia_trasmessa_at).getTime() : 0;
              const dbb = b.denuncia_trasmessa_at ? new Date(b.denuncia_trasmessa_at).getTime() : 0;
              if (da === 0 && dbb === 0) return 0;
              if (da === 0) return 1;
              if (dbb === 0) return -1;
              return dbb - da;
            });
          }
          if (visible.length === 0) {
            return <Card className="p-5 text-center text-slate-500 text-lg border-2" data-testid="sinistri-empty">
              {list.length === 0 ? "Nessuna pratica di sinistro registrata." : "Nessuna pratica corrisponde ai filtri."}
            </Card>;
          }
          return visible.map((s) => {
          const bordoStato = getBandSinistro(s);
          return (
          <SharedCard key={s.id} band={bordoStato} data-testid={`sinistro-card-${s.numero_pratica}`} className="p-3 border-2 hover:border-[#C98AA0] transition-colors">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs font-bold text-slate-500">{s.numero_pratica}</span>
                  <FaseBadge fase={s.fase} />
                  {/* Iter122k150 · Badge di stato pagamento anche sulla card lista:
                      • "ATTESA PAGAMENTO" — indennizzabile, pagamento non confermato, pratica non chiusa
                      • "PAGATO · CHIUSO" — indennizzabile, pagamento confermato e pratica chiusa
                      Aumenta la leggibilità del follow-up sulle pratiche liquidate. */}
                  {s.esito_valutazione === "indennizzabile" && !s.pagamento_confermato
                    && s.fase !== "Chiuso-Liquidato" && (
                    <span data-testid={`sinistro-attesa-pagamento-${s.numero_pratica}`}
                      className="px-1.5 py-0.5 rounded text-xs font-bold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-400">
                      Attesa pagamento
                    </span>
                  )}
                  {s.esito_valutazione === "indennizzabile" && s.pagamento_confermato
                    && s.fase === "Chiuso-Liquidato" && (
                    <span data-testid={`sinistro-pagato-chiuso-${s.numero_pratica}`}
                      className="px-1.5 py-0.5 rounded text-xs font-bold uppercase tracking-wide bg-emerald-100 text-emerald-800 border border-emerald-400">
                      Pagato · Chiuso
                    </span>
                  )}
                  {/* Iter122k151 · Card lista: chip rosso "Non indennizzabile" accanto a "Chiuso"
                      per pratiche chiuse con esito negativo (parallelo di "Pagato · Chiuso"). */}
                  {s.esito_valutazione === "non_indennizzabile"
                    && s.fase === "Chiuso-Liquidato" && (
                    <span data-testid={`sinistro-non-indennizzabile-${s.numero_pratica}`}
                      className="px-1.5 py-0.5 rounded text-xs font-bold uppercase tracking-wide bg-red-100 text-red-800 border border-red-400">
                      Non indennizzabile
                    </span>
                  )}
                  {/* Iter122k20 · Numero sinistro assegnato dalla compagnia (visibile a colpo d'occhio nella card) */}
                  {s.numero_sinistro && (
                    <span data-testid={`sinistro-numero-badge-${s.numero_pratica}`}
                      className="px-1.5 py-0.5 bg-brand/10 text-brand border border-brand/30 rounded text-xs font-bold flex items-center gap-1"
                      title="Numero sinistro assegnato dalla compagnia">
                      N. sinistro: {s.numero_sinistro}
                    </span>
                  )}
                  {(() => {
                    const nums = (s.ticket_numeri && s.ticket_numeri.length)
                      ? s.ticket_numeri
                      : (s.ticket_numero ? [s.ticket_numero] : []);
                    return nums.map((n) => (
                      <span key={n} className="px-1.5 py-0.5 bg-slate-100 rounded text-xs font-semibold flex items-center gap-1">
                        <FileText className="w-3 h-3" /> {n}
                      </span>
                    ));
                  })()}
                </div>
                <div className="font-semibold text-base truncate mt-0.5">{s.descrizione || "Sinistro"}</div>
                {/* Iter122k47 · Data sinistro in rosso, sempre visibile sulla card */}
                {s.data_sinistro && (
                  <div className="text-xs font-bold text-red-700 mt-0.5 flex items-center gap-1" data-testid={`sinistro-data-${s.numero_pratica}`}>
                    <Calendar className="w-3.5 h-3.5" />
                    Data sinistro: {new Date(s.data_sinistro).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" })}
                  </div>
                )}
                <div className="text-xs text-slate-500 flex items-center gap-1 truncate">
                  <Building2 className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{s.condominio_nome}</span>
                  {s.compagnia_nome && <span className="truncate">· {s.compagnia_nome}</span>}
                </div>
                {/* Iter122jd · Data invio denuncia (se già trasmessa) */}
                {s.denuncia_trasmessa_at && (
                  <div className="text-xs text-emerald-700 mt-0.5" data-testid={`sinistro-denuncia-data-${s.numero_pratica}`}>
                    <b>Denuncia inviata:</b> {new Date(s.denuncia_trasmessa_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" })}
                  </div>
                )}
                {(s.condominio_codice_fiscale || s.condominio_codice_sdi) && (
                  <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3">
                    {s.condominio_codice_fiscale && (
                      <span data-testid={`sinistro-cf-${s.numero_pratica}`}>
                        <b className="text-slate-600">C.F.:</b> {s.condominio_codice_fiscale}
                      </span>
                    )}
                    {s.condominio_codice_sdi && (
                      <span data-testid={`sinistro-sdi-${s.numero_pratica}`}>
                        <b className="text-slate-600">SDI:</b> {s.condominio_codice_sdi}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex gap-1 shrink-0">
                {/* Iter122iz · Badge push non letti (messaggi + documenti dal condòmino) sulla card */}
                {((s.messaggi_non_letti || 0) + (s.doc_nuovi || 0)) > 0 && (
                  <span data-testid={`sinistro-unread-${s.numero_pratica}`}
                    data-count-msg={s.messaggi_non_letti || 0}
                    data-count-doc={s.doc_nuovi || 0}
                    title={`${s.messaggi_non_letti || 0} messaggi · ${s.doc_nuovi || 0} nuovi documenti dal condòmino`}
                    className="inline-flex items-center gap-1 h-8 px-2 rounded-full bg-red-600 text-white text-xs font-bold animate-pulse shadow-sm">
                    <BellRing className="w-3.5 h-3.5" />
                    {(s.messaggi_non_letti || 0) + (s.doc_nuovi || 0)}
                  </span>
                )}
                <Button data-testid={`edit-sinistro-${s.numero_pratica}`} variant="outline" size="sm" className="h-8 px-2 text-xs" onClick={() => openEdit(s)}>
                  <Pencil className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Gestisci</span>
                </Button>
                {/* Iter122k153 · Cestino: mostra Ripristina + Elimina definitivo invece di Cestina */}
                {filterFase === "cestino" ? (
                  <>
                    <Button data-testid={`restore-sinistro-${s.numero_pratica}`}
                      variant="outline" size="sm"
                      className="h-8 px-2 text-emerald-700 border-emerald-300 hover:bg-emerald-50 text-xs"
                      onClick={() => ripristina(s.id)}>
                      <RotateCcw className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Ripristina</span>
                    </Button>
                    <Button data-testid={`purge-sinistro-${s.numero_pratica}`}
                      variant="outline" size="sm"
                      className="h-8 px-2 text-red-700 border-red-400 hover:bg-red-50"
                      title="Elimina definitivamente"
                      onClick={() => rimuoviDefinitivo(s.id, s.numero_pratica)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </>
                ) : (
                  <Button data-testid={`delete-sinistro-${s.numero_pratica}`} variant="outline" size="sm" className="h-8 px-2 text-red-700 border-red-300" onClick={() => remove(s.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </SharedCard>
          );
        });
        })()}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[92vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6">
          <DialogHeader className="min-w-0"><DialogTitle className="text-xl sm:text-2xl flex items-center gap-2 min-w-0"><ShieldAlert className="w-6 h-6 text-amber-600 shrink-0" /> <span className="truncate">{editId ? "Gestione pratica" : "Nuova pratica sinistro"}</span></DialogTitle></DialogHeader>
          {/* Iter122ij · Banner informativo per fornitore Compagnia/Agenzia in Istruttoria */}
          {readOnlyMode && (
            <div className="mb-3 p-3 rounded-lg border-2 border-amber-300 bg-amber-50 text-sm text-amber-900 min-w-0 break-words" data-testid="forn-readonly-banner">
              <b>Modalità sola visualizzazione.</b> Come Compagnia/Agenzia puoi modificare esclusivamente il <b>N° Sinistro</b>, i dati del <b>Perito</b> e del <b>Liquidatore</b>, caricare documenti (Gestione › Documenti) e usare la chat "Studio ↔ Compagnia/Agenzia". Gli altri campi sono in sola lettura.
            </div>
          )}
          {/* Iter122jg · min-w-0 sui figli diretti del grid Dialog: evita che stringhe lunghe (filename/email) stirino il modale oltre 95vw su mobile */}
          <div className="space-y-4 min-w-0">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-bold">Condominio<span className="text-red-500 ml-0.5">*</span></Label>
                <select data-testid="sinistro-condominio" value={form.condominio_id} onChange={(e) => onCondominioChange(e.target.value)}
                  className="h-12 mt-1 w-full border-2 border-slate-400 rounded-lg px-3 bg-white">
                  <option value="">Seleziona...</option>
                  {condomini.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-sm font-bold">Guasti collegati (opz.)</Label>
                {/* Iter122ik · Multi-select: dropdown "aggiungi" + chips per rimozione */}
                <select data-testid="sinistro-ticket-add"
                  value=""
                  onChange={(e) => {
                    const tid = e.target.value;
                    if (!tid) return;
                    const current = form.ticket_ids && form.ticket_ids.length ? form.ticket_ids : (form.ticket_id ? [form.ticket_id] : []);
                    if (current.includes(tid)) return;
                    const next = [...current, tid];
                    setForm((f) => ({ ...f, ticket_ids: next, ticket_id: next[0] }));
                  }}
                  className="h-12 mt-1 w-full border-2 border-slate-400 rounded-lg px-3 bg-white">
                  <option value="">+ Aggiungi guasto…</option>
                  {tickets
                    .filter((t) => !form.condominio_id || t.condominio_id === form.condominio_id)
                    .filter((t) => !((form.ticket_ids || (form.ticket_id ? [form.ticket_id] : [])).includes(t.id)))
                    .map((t) => <option key={t.id} value={t.id}>{t.numero} — {t.condominio_nome} — {t.categoria || t.titolo}</option>)}
                </select>
                {(() => {
                  const list = form.ticket_ids && form.ticket_ids.length ? form.ticket_ids : (form.ticket_id ? [form.ticket_id] : []);
                  if (!list.length) return <p className="text-xs text-slate-500 mt-1">Puoi collegare più manutenzioni: verranno aggregati foto/documenti nel PDF denuncia e in Gestione › Documenti.</p>;
                  return (
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="sinistro-ticket-chips">
                      {list.map((tid) => {
                        const t = tickets.find((x) => x.id === tid);
                        return (
                          <span key={tid} data-testid={`ticket-chip-${tid}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 border border-slate-300 text-xs font-semibold">
                            {t ? t.numero : tid}
                            <button type="button" onClick={() => {
                              const next = list.filter((x) => x !== tid);
                              setForm((f) => ({ ...f, ticket_ids: next, ticket_id: next[0] || null }));
                            }} className="ml-0.5 hover:text-red-600" data-testid={`ticket-chip-remove-${tid}`} aria-label="Rimuovi guasto">
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  );
                })()}
                {form.condominio_id && tickets.filter((t) => t.condominio_id === form.condominio_id).length === 0 && (
                  <p className="text-sm text-slate-500 mt-1">Nessuna manutenzione per questo condominio.</p>
                )}
              </div>
            </div>

            {(() => { const c = condomini.find((x) => x.id === form.condominio_id); return c ? <CondInsuranceInfo c={c} /> : null; })()}

            {/* Iter123k172 · Amministratore legale rappresentante — precompilato dal condominio, editabile */}
            <div>
              <Label className="text-sm font-bold">
                Amministratore legale rapp.te<span className="text-red-600 ml-0.5" title="Campo obbligatorio">*</span>
              </Label>
              <Input data-testid="sinistro-amministratore-legale"
                value={form.amministratore_legale || ""}
                onChange={(e) => set("amministratore_legale", e.target.value)}
                className="mt-1 h-11 bg-white"
                placeholder="Nome del legale rappresentante del condominio (es. Studio Izzo — Rag. G. Izzo)" />
              <p className="text-[10px] text-slate-500 mt-1">
                Recuperato in automatico dal campo &quot;Amministratore legale rapp.te&quot; della scheda del condominio (Stabili). Modificabile per il singolo sinistro.
              </p>
            </div>

            {/* Iter122h8/k118 · N° Sinistro (assegnato dalla compagnia) — visibile dalla fase Istruttoria in poi.
                Iter122k118 · Riquadro ROSSO se manca il numero, VERDE quando compilato — evidenza a colpo d'occhio. */}
            {editId && form.fase !== "Bozza-Denuncia" && (
              <div
                data-testid="sinistro-numero-compagnia-box"
                data-filled={form.numero_sinistro ? "true" : "false"}
                className={`border-2 rounded-lg p-3 transition-colors ${
                  form.numero_sinistro
                    ? "border-emerald-400 bg-emerald-50"
                    : "border-red-400 bg-red-50"
                }`}
              >
                <Label className={`text-sm font-bold ${form.numero_sinistro ? "text-emerald-900" : "text-red-900"}`}>
                  N° Sinistro (assegnato dalla compagnia)
                  {form.numero_sinistro
                    ? <span className="ml-2 text-[10px] font-bold text-emerald-700">✓ ASSEGNATO</span>
                    : <span className="ml-2 text-[10px] font-bold text-red-700">⚠ MANCANTE</span>}
                </Label>
                <Input data-testid="sinistro-numero-compagnia"
                  value={form.numero_sinistro || ""} onChange={(e) => set("numero_sinistro", e.target.value)}
                  className="mt-1 h-11 bg-white" placeholder="Es. 2026/000123 · lasciare vuoto finché non lo comunica la compagnia" />
                <p className="text-[11px] text-slate-500 mt-1">Numero pratica interno: <b className="font-mono">{form.numero_pratica || "—"}</b></p>
              </div>
            )}

            <div>
              <Label className="text-sm font-bold">Data del sinistro<span className="text-red-600 ml-0.5" title="Campo obbligatorio per uscire dalla Bozza">*</span></Label>
              <Input type="date" data-testid="sinistro-data-sinistro" value={form.data_sinistro || ""} onChange={(e) => set("data_sinistro", e.target.value)} className="mt-1 h-11 bg-white" />
              <p className="text-[10px] text-slate-500 mt-1">Data dell&apos;evento dannoso — comparirà nella denuncia inviata a Compagnia/Agenzia.</p>
            </div>

            <div>
              <Label className="text-sm font-bold">Oggetto del sinistro<span className="text-red-600 ml-0.5" title="Campo obbligatorio per uscire dalla Bozza">*</span></Label>
              <Input data-testid="sinistro-oggetto" value={form.oggetto || ""} onChange={(e) => set("oggetto", e.target.value)} className="mt-1 h-11 bg-white" placeholder="Es. Allagamento box auto - Piano interrato" />
            </div>

            <div>
              <Label className="text-sm font-bold">Descrizione del sinistro<span className="text-red-600 ml-0.5" title="Campo obbligatorio per uscire dalla Bozza">*</span></Label>
              <Textarea data-testid="sinistro-descrizione" value={form.descrizione} onChange={(e) => set("descrizione", e.target.value)} className="mt-1 bg-white" placeholder="Es. Allagamento garage da rottura tubazione" />
            </div>

            {/* Iter122fk · Interventi eseguiti (rintracci/urgenze) — flag + descrizione */}
            <div className="border-2 border-slate-200 rounded-lg p-3 bg-slate-50/40">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label className="text-sm font-bold">Interventi eseguiti (rintracci/interventi urgenti)</Label>
                <div className="flex items-center gap-2" data-testid="sinistro-interventi-toggle">
                  <button type="button" data-testid="sinistro-interventi-si"
                    onClick={() => set("interventi_urgenti", true)}
                    className={`px-4 py-1.5 rounded-full border-2 text-sm font-bold ${form.interventi_urgenti ? "border-amber-600 bg-amber-600 text-white" : "border-slate-400 bg-white text-slate-600 hover:border-amber-400"}`}>
                    Sì
                  </button>
                  <button type="button" data-testid="sinistro-interventi-no"
                    onClick={() => setForm((f) => ({ ...f, interventi_urgenti: false, interventi_descrizione: "" }))}
                    className={`px-4 py-1.5 rounded-full border-2 text-sm font-bold ${!form.interventi_urgenti ? "border-slate-600 bg-slate-700 text-white" : "border-slate-400 bg-white text-slate-600 hover:border-slate-400"}`}>
                    No
                  </button>
                </div>
              </div>
              {form.interventi_urgenti && (
                <div className="mt-3" data-testid="sinistro-interventi-desc-wrap">
                  <Label className="text-sm font-semibold">Descrizione esito intervento</Label>
                  <Textarea data-testid="sinistro-interventi-descrizione" value={form.interventi_descrizione || ""}
                    onChange={(e) => set("interventi_descrizione", e.target.value)}
                    className="mt-1" rows={3}
                    placeholder="Es. Rintraccio del guasto e intervento di emergenza del fornitore idraulico in data 12/03, chiusura provvisoria della perdita" />
                </div>
              )}
            </div>

            <div>
              <Label className="text-sm font-bold">Note aggiuntive</Label>
              <Textarea data-testid="sinistro-note-aggiuntive" value={form.note_aggiuntive || ""}
                onChange={(e) => set("note_aggiuntive", e.target.value)}
                className="mt-1" rows={3}
                placeholder="Note libere: dettagli logistici, testimoni, riferimenti pratiche collegate…" />
            </div>

            {/* Iter122cs · Stepper 5 fasi */}
            <FaseStepper fase={form.fase} onAvanza={avanzaFase} editable={!!editId}
              esito={form.esito_valutazione} pagamentoConfermato={!!form.pagamento_confermato} />

            {/* Iter122cs · Warning transizione fase */}
            {transizioneWarnings && (
              <div className="border-2 border-amber-300 bg-amber-50 rounded-lg p-3" data-testid="fase-warnings">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-amber-900 text-sm mb-1">
                      Attenzione — passaggio a &laquo;{FASE_LABEL[transizioneWarnings.fase] || transizioneWarnings.fase}&raquo;
                    </div>
                    <ul className="text-sm text-amber-900 list-disc pl-5 space-y-0.5">
                      {transizioneWarnings.warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                    <div className="mt-2 flex gap-2 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => setTransizioneWarnings(null)} data-testid="fase-warning-annulla">Annulla</Button>
                      <Button size="sm" onClick={() => avanzaFase(transizioneWarnings.fase, true)} data-testid="fase-warning-conferma"
                        className="bg-amber-600 hover:bg-amber-700 text-white">Procedi comunque</Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Iter122cs · Main tabs SOGGETTI / GESTIONE */}
            <div className="border-b-2 border-slate-200 flex flex-wrap items-center gap-1" data-testid="sinistro-main-tabs">
              <button type="button" data-testid="tab-soggetti"
                onClick={() => setMainTab("soggetti")}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 font-bold text-sm border-b-[3px] -mb-0.5 rounded-t-md transition-colors ${mainTab === "soggetti" ? "bg-amber-50 border-amber-500 text-amber-800" : "border-transparent text-slate-500 hover:text-amber-700 hover:bg-amber-50/40"}`}>
                <Users className="w-4 h-4" /> Soggetti
              </button>
              <button type="button" data-testid="tab-gestione"
                onClick={() => setMainTab("gestione")}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 font-bold text-sm border-b-[3px] -mb-0.5 rounded-t-md transition-colors ${mainTab === "gestione" ? "bg-indigo-50 border-indigo-500 text-indigo-800" : "border-transparent text-slate-500 hover:text-indigo-700 hover:bg-indigo-50/40"}`}>
                <Folder className="w-4 h-4" /> Gestione
              </button>
            </div>

            {/* PANE: SOGGETTI */}
            {mainTab === "soggetti" && (
              <div className="space-y-3" data-testid="pane-soggetti">
                <SubTabs testidPrefix="soggetti"
                  tabs={[
                    // Iter122h8 · Compagnia · Agenzia nascosta in fase Istruttoria
                    ...(form.fase !== "Aperto-Istruttoria" ? [
                      { key: "compagnia", label: "Compagnia · Agenzia", icon: Umbrella },
                    ] : []),
                    { key: "danneggiati", label: "Danneggiato/i", icon: Users },
                    { key: "danneggianti", label: "Danneggiante/i", icon: Users },
                    // Iter122h7 · Perito e Liquidatore compaiono solo dalle fasi post-denuncia
                    ...(form.fase !== "Bozza-Denuncia" ? [
                      { key: "perito", label: "Perito", icon: HardHat },
                      { key: "liquidatore", label: "Liquidatore", icon: Scale },
                    ] : []),
                  ]}
                  openSet={openSoggetti} onToggle={toggleSoggetti}
                />

                {openSoggetti.has("compagnia") && form.fase !== "Aperto-Istruttoria" && (
                  <div className="space-y-3">
                    <Section title="Compagnia assicurativa">
                      <div className="sm:col-span-2 flex items-center gap-2 p-2 bg-emerald-50 border-2 border-emerald-200 rounded-lg">
                        <input type="checkbox" id="chk-invia-compagnia" data-testid="chk-invia-compagnia"
                          checked={!!form.invia_a_compagnia} onChange={(e) => set("invia_a_compagnia", e.target.checked)}
                          className="w-4 h-4 accent-emerald-600" />
                        <label htmlFor="chk-invia-compagnia" className="text-sm font-semibold text-emerald-900 cursor-pointer">
                          Invia la denuncia alla Compagnia <span className="font-normal text-emerald-700">(deseleziona per non inviare)</span>
                        </label>
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-sm font-semibold">Seleziona compagnia (fornitori cat. Assicurazione)</Label>
                        <select data-testid="sinistro-compagnia-select" value={form.compagnia_fornitore_id || ""} onChange={(e) => selectAssicuratore("compagnia", e.target.value)}
                          className="h-11 mt-1 w-full border-2 border-slate-400 rounded-lg px-3 bg-white">
                          <option value="">— scegli o compila manualmente —</option>
                          {assicuratori.map((a) => <option key={a.id} value={a.id}>{a.ragione_sociale}{a.email ? ` (${a.email})` : ""}</option>)}
                        </select>
                      </div>
                      <F label="Nome compagnia" v={form.compagnia_nome} on={(v) => set("compagnia_nome", v)} testid="sinistro-compagnia" required />
                      <F label="Email compagnia (per denuncia)" v={form.compagnia_email} on={(v) => set("compagnia_email", v)} testid="sinistro-compagnia-email" placeholder="email@compagnia.it" />
                      <F label="PEC compagnia (per accordo)" v={form.compagnia_pec} on={(v) => set("compagnia_pec", v)} testid="sinistro-compagnia-pec" placeholder="pec@compagnia.it" />
                      <F label="Contatti compagnia" v={form.compagnia_contatti} on={(v) => set("compagnia_contatti", v)} />
                      <F label="Numero polizza" v={form.numero_polizza} on={(v) => set("numero_polizza", v)} />
                      <F label="Scadenza polizza" v={form.polizza_scadenza} on={(v) => set("polizza_scadenza", v)} placeholder="gg/mm/aaaa" />
                    </Section>

                    <Section title="Agenzia (destinataria denuncia)">
                      <div className="sm:col-span-2 flex items-center gap-2 p-2 bg-emerald-50 border-2 border-emerald-200 rounded-lg">
                        <input type="checkbox" id="chk-invia-agenzia" data-testid="chk-invia-agenzia"
                          checked={!!form.invia_a_agenzia} onChange={(e) => set("invia_a_agenzia", e.target.checked)}
                          className="w-4 h-4 accent-emerald-600" />
                        <label htmlFor="chk-invia-agenzia" className="text-sm font-semibold text-emerald-900 cursor-pointer">
                          Invia la denuncia all&apos;Agenzia <span className="font-normal text-emerald-700">(deseleziona per non inviare)</span>
                        </label>
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-sm font-semibold">Seleziona agenzia (fornitori cat. Assicurazione)</Label>
                        <select data-testid="sinistro-agenzia-select" value={form.agenzia_fornitore_id || ""} onChange={(e) => selectAssicuratore("agenzia", e.target.value)}
                          className="h-11 mt-1 w-full border-2 border-slate-400 rounded-lg px-3 bg-white">
                          <option value="">— scegli o compila manualmente —</option>
                          {assicuratori.map((a) => <option key={a.id} value={a.id}>{a.ragione_sociale}{a.email ? ` (${a.email})` : ""}</option>)}
                        </select>
                      </div>
                      <F label="Nome agenzia" v={form.agenzia_nome} on={(v) => set("agenzia_nome", v)} testid="sinistro-agenzia" required />
                      <F label="Email agenzia (per denuncia)" v={form.agenzia_email} on={(v) => set("agenzia_email", v)} testid="sinistro-agenzia-email" placeholder="email@agenzia.it" />
                      <F label="PEC agenzia (per accordo)" v={form.agenzia_pec} on={(v) => set("agenzia_pec", v)} testid="sinistro-agenzia-pec" placeholder="pec@agenzia.it" />
                      <F label="Contatti agenzia" v={form.agenzia_contatti} on={(v) => set("agenzia_contatti", v)} />
                    </Section>
                  </div>
                )}

                {openSoggetti.has("danneggiati") && (
                  <div className="space-y-3">
                    <div className="border-2 border-[#EBC5D2] rounded-lg p-4 bg-[#FBEEF3]/40">
                      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                        <div className="text-sm font-bold uppercase tracking-wide text-brand flex items-center gap-2"><Users className="w-4 h-4" /> Danneggiato/i</div>
                        <Button type="button" data-testid="add-danneggiato-button" variant="outline" size="sm" className="h-9" onClick={addDanneggiato}>
                          <Plus className="w-4 h-4 mr-1" /> Aggiungi manualmente
                        </Button>
                      </div>
                      {/* Iter122fp13 · Popola da rubrica UNICO (pattern chip come i fornitori in Preventivi/Manutenzione) */}
                      {contattiCond.length > 0 && (
                        <div className="mb-3">
                          <Label className="text-xs font-semibold text-slate-600">Aggiungi da rubrica del condominio</Label>
                          <select data-testid="danneggiato-picker-rubrica" value=""
                            onChange={(e) => { addFromRubrica("danneggiato", e.target.value); e.target.value = ""; }}
                            className="mt-1 h-10 w-full rounded-lg border-2 border-slate-400 px-3 text-sm bg-white">
                            <option value="">— Seleziona un soggetto per aggiungerlo —</option>
                            {contattiCond.map((c) => {
                              const key = c.id || c.soggetto_id;
                              const sub = [c.ruolo, c.unita].filter(Boolean).join(" · ");
                              return <option key={key} value={key}>{c.nome || c.ragione_sociale || "?"}{sub ? ` (${sub})` : ""}</option>;
                            })}
                          </select>
                          {(form.danneggiati || []).length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="danneggiati-chips">
                              {(form.danneggiati || []).map((d, i) => (
                                <span key={i} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full bg-brand text-white text-sm font-semibold">
                                  <span className="max-w-[220px] truncate">{d.nome || `Danneggiato ${i + 1}`}</span>
                                  <button type="button" data-testid={`danneggiato-chip-remove-${i}`}
                                    onClick={() => removeDanneggiato(i)}
                                    className="hover:bg-red-600/30 rounded-full p-0.5 transition-colors" aria-label="Rimuovi">
                                    <X className="w-4 h-4" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      {(form.danneggiati || []).length === 0 && <div className="text-base text-slate-500">Nessun danneggiato. Aggiungine uno dalla rubrica o manualmente.</div>}
                      <div className="space-y-4">
                        {(form.danneggiati || []).map((d, i) => (
                          <div key={i} data-testid={`danneggiato-${i}`} className="border-2 border-slate-200 rounded-lg p-3 bg-white">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-bold text-brand">Danneggiato {i + 1}</span>
                              <button type="button" data-testid={`remove-danneggiato-${i}`} onClick={() => removeDanneggiato(i)} className="text-red-600 hover:text-red-700"><X className="w-4 h-4" /></button>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-3">
                              <F label="Nome e cognome" v={d.nome} on={(v) => updateDanneggiato(i, "nome", v)} testid={`danneggiato-nome-${i}`} required />
                              <F label="Telefono / contatti" v={d.contatti} on={(v) => updateDanneggiato(i, "contatti", v)} testid={`danneggiato-contatti-${i}`} />
                              <F label="Email" v={d.email || ""} on={(v) => updateDanneggiato(i, "email", v)} testid={`danneggiato-email-${i}`} type="email" />
                              <F label="Domicilio / Residenza" v={d.residenza || ""} on={(v) => updateDanneggiato(i, "residenza", v)} testid={`danneggiato-residenza-${i}`} />
                            </div>
                            <div className="mt-2">
                              <Label className="text-sm font-semibold">Descrizione del danno</Label>
                              <Textarea data-testid={`danneggiato-descrizione-${i}`} value={d.descrizione} onChange={(e) => updateDanneggiato(i, "descrizione", e.target.value)} className="mt-1" />
                            </div>
                            {/* Iter122k38 · Polizza privata del danneggiato (facoltativa)
                                Iter122k103 · gated by flag `has_polizza_privata` (default False) */}
                            <div className="mt-2 rounded-lg border-2 border-amber-200 bg-amber-50/40 p-3" data-testid={`danneggiato-polizza-${i}`}>
                              <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div>
                                  <Label className="text-sm font-bold uppercase tracking-wide text-amber-900">Polizza privata</Label>
                                  <p className="text-xs text-amber-800/80 mt-0.5">Attiva se il danneggiato ha una propria polizza attivabile sul sinistro. Il condòmino riceverà una notifica per completare i dati.</p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-semibold text-slate-600">No</span>
                                  <Switch data-testid={`danneggiato-polizza-flag-${i}`}
                                    checked={!!d.has_polizza_privata}
                                    onCheckedChange={(v) => updateDanneggiato(i, "has_polizza_privata", v)} />
                                  <span className="text-xs font-semibold text-amber-900">Sì</span>
                                </div>
                              </div>
                              {d.has_polizza_privata && (
                                <div className="mt-3 grid sm:grid-cols-2 gap-3">
                                  <F label="N. sinistro (polizza privata)" v={d.polizza_privata_numero || ""}
                                     on={(v) => updateDanneggiato(i, "polizza_privata_numero", v)}
                                     testid={`danneggiato-polizza-numero-${i}`} />
                                  <F label="Compagnia (denominazione)" v={d.polizza_privata_compagnia_nome || ""}
                                     on={(v) => updateDanneggiato(i, "polizza_privata_compagnia_nome", v)}
                                     testid={`danneggiato-polizza-compagnia-${i}`} />
                                  <F label="Compagnia · contatti (tel/email)" v={d.polizza_privata_compagnia_contatti || ""}
                                     on={(v) => updateDanneggiato(i, "polizza_privata_compagnia_contatti", v)}
                                     testid={`danneggiato-polizza-compagnia-contatti-${i}`} />
                                  <F label="Perito (nominativo)" v={d.polizza_privata_perito_nome || ""}
                                     on={(v) => updateDanneggiato(i, "polizza_privata_perito_nome", v)}
                                     testid={`danneggiato-polizza-perito-${i}`} />
                                  <F label="Perito · contatti (tel/email)" v={d.polizza_privata_perito_contatti || ""}
                                     on={(v) => updateDanneggiato(i, "polizza_privata_perito_contatti", v)}
                                     testid={`danneggiato-polizza-perito-contatti-${i}`} />
                                </div>
                              )}
                            </div>
                            <div className="mt-2">
                              <Label className="text-sm font-semibold">Foto del danno</Label>
                              <div className="mt-1"><PhotoUploader photos={d.foto || []} onChange={(v) => updateDanneggiato(i, "foto", v)} testid={`danneggiato-foto-${i}`} /></div>
                            </div>
                            <div className="mt-2">
                              <Label className="text-sm font-semibold">File / documenti</Label>
                              <div className="mt-1"><DocumentUploader docs={d.allegati || []} onChange={(v) => updateDanneggiato(i, "allegati", v)} testid={`danneggiato-doc-${i}`} /></div>
                            </div>
                            {/* Iter122k25 · Richiesta documentazione LEGATA al singolo danneggiato */}
                            <div className="mt-3 rounded-lg border-2 border-blue-200 bg-blue-50/40 p-3" data-testid={`danneggiato-docreq-${i}`}>
                              <div className="flex items-center justify-between gap-2 flex-wrap">
                                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-blue-800">
                                  <BellRing className="w-3.5 h-3.5" /> Documentazione al condòmino
                                </div>
                                <button type="button" data-testid={`danneggiato-docreq-toggle-${i}`}
                                  onClick={() => {
                                    const nuovo = !d.doc_richiesta;
                                    updateDanneggiato(i, "doc_richiesta", nuovo);
                                    // Sync top-level: attivo se almeno un danneggiato lo è
                                    const anyActive = nuovo || (form.danneggiati || []).some((x, k) => k !== i && x.doc_richiesta);
                                    set("documentazione_richiesta", anyActive);
                                  }}
                                  className={`px-2.5 py-1 rounded-full border-2 text-xs font-bold ${d.doc_richiesta ? "border-blue-600 bg-blue-600 text-white" : "border-slate-400 bg-white text-slate-600"}`}>
                                  {d.doc_richiesta ? "Richiesta attiva" : "Richiedi documentazione"}
                                </button>
                              </div>
                              {d.doc_richiesta && (
                                <div className="mt-2 grid sm:grid-cols-2 gap-2 items-end">
                                  <div>
                                    <Label className="text-xs font-semibold">Termine invio (opz.)</Label>
                                    <Input type="date" data-testid={`danneggiato-docreq-scad-${i}`}
                                      value={d.doc_scadenza || ""}
                                      onChange={(e) => {
                                        updateDanneggiato(i, "doc_scadenza", e.target.value);
                                        // Sync top-level scadenza = più vicina tra le attive
                                        const dates = (form.danneggiati || []).map((x, k) => k === i ? e.target.value : x.doc_scadenza)
                                          .filter((v, k) => v && (form.danneggiati || [])[k]?.doc_richiesta);
                                        set("documentazione_scadenza", dates.sort()[0] || "");
                                      }}
                                      className="mt-1 h-10 bg-white" />
                                  </div>
                                  {editId && (
                                    <Button type="button" data-testid={`danneggiato-docreq-sollecita-${i}`}
                                      variant="outline" onClick={sollecitaDoc}
                                      className="h-10 border-2 border-blue-500 text-blue-700 font-bold text-xs">
                                      <BellRing className="w-3.5 h-3.5 mr-1" /> Sollecita ora
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Iter122k25 · La richiesta documentazione è stata spostata dentro ogni singolo danneggiato (vedi sopra).
                        Qui resta solo l'elenco della documentazione già ricevuta (comune al sinistro). */}
                    <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50/40" data-testid="sinistro-doc-section">
                      <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-blue-800">
                        <BellRing className="w-4 h-4" /> Documentazione ricevuta
                        {(() => {
                          const s = list.find((x) => x.id === editId);
                          const nDoc = s?.doc_nuovi || 0;
                          const nMsg = s?.messaggi_non_letti || 0;
                          const tot = nDoc + nMsg;
                          if (tot === 0) return null;
                          return (
                            <span data-testid="sinistro-doc-cond-unread"
                              data-count={tot}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-600 text-white text-xs font-bold animate-pulse">
                              {tot} nuov{tot === 1 ? "o" : "i"}
                            </span>
                          );
                        })()}
                      </div>
                      <p className="text-xs text-slate-600 mt-1">
                        Le richieste vengono ora inviate <b>separatamente</b> a ciascun danneggiato: gestisci la richiesta nel riquadro di ognuno.
                      </p>
                      {/* Iter122iy · Elenco della documentazione integrativa caricata dal condòmino (o dallo Studio) */}
                      {(form.documentazione_condomino || []).length > 0 && (
                        <div className="mt-4 space-y-2" data-testid="documentazione-condomino-list">
                          <div className="text-xs font-bold uppercase tracking-wide text-blue-700">Documentazione ricevuta ({form.documentazione_condomino.length})</div>
                          {form.documentazione_condomino.map((v) => {
                            const foto = v.foto || [];
                            const allegati = v.allegati || [];
                            const created = v.created_at ? new Date(v.created_at).toLocaleString("it-IT") : "";
                            const author = v.author_nome || (v.author_role === "condomino" ? "Condòmino" : "Studio");
                            return (
                              <div key={v.id} className="rounded-lg border border-blue-200 bg-white p-3" data-testid={`doc-cond-voce-${v.id}`}>
                                <div className="flex items-center gap-2 text-xs text-slate-600 mb-1 flex-wrap">
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${v.author_role === "condomino" ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-700"}`}>
                                    <Users className="w-3 h-3" /> {author}
                                  </span>
                                  {created && <span className="text-slate-500">{created}</span>}
                                </div>
                                {(v.testo || "").trim() && (
                                  <p className="text-sm text-slate-800 whitespace-pre-wrap mb-2">{v.testo}</p>
                                )}
                                {foto.length > 0 && (
                                  <div className="flex flex-wrap gap-2 mb-2">
                                    {foto.map((f) => (
                                      <button key={f.id} type="button" data-testid={`doc-cond-foto-${f.id}`}
                                        onClick={() => openSecureFile(`/files/${f.id}`)}
                                        className="w-20 h-20 rounded border-2 border-blue-200 overflow-hidden bg-slate-100 hover:border-blue-400"
                                        title={f.filename || "Foto"}>
                                        <AuthImage fileId={f.id} className="w-full h-full object-cover" />
                                      </button>
                                    ))}
                                  </div>
                                )}
                                {allegati.length > 0 && (
                                  <div className="flex flex-col gap-1.5">
                                    {allegati.map((a) => (
                                      <button key={a.id} type="button"
                                        onClick={() => openSecureFile(`/files/${a.id}`)}
                                        data-testid={`doc-cond-allegato-${a.id}`}
                                        className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded border-2 border-slate-200 hover:border-blue-400 text-sm text-blue-700 font-semibold w-full min-w-0 sm:w-fit sm:max-w-full">
                                        <FileText className="w-4 h-4 shrink-0" />
                                        <span className="truncate min-w-0 flex-1 text-left">{a.filename || "Documento"}</span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Iter122fk · Danneggiante/i — soggetti terzi presunti responsabili */}
                {openSoggetti.has("danneggianti") && (
                  <div className="space-y-3">
                    <div className="border-2 border-orange-200 rounded-lg p-4 bg-orange-50/40" data-testid="pane-danneggianti">
                      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                        <div className="text-sm font-bold uppercase tracking-wide text-orange-800 flex items-center gap-2"><Users className="w-4 h-4" /> Danneggiante/i</div>
                        <Button type="button" data-testid="add-danneggiante-button" variant="outline" size="sm" className="h-9" onClick={addDanneggiante}>
                          <Plus className="w-4 h-4 mr-1" /> Aggiungi manualmente
                        </Button>
                      </div>
                      {/* Iter122fp13 · Popola da rubrica UNICO con chip strip (allineato al pattern fornitori) */}
                      {contattiCond.length > 0 && (
                        <div className="mb-3">
                          <Label className="text-xs font-semibold text-slate-600">Aggiungi da rubrica del condominio</Label>
                          <select data-testid="danneggiante-picker-rubrica" value=""
                            onChange={(e) => { addFromRubrica("danneggiante", e.target.value); e.target.value = ""; }}
                            className="mt-1 h-10 w-full rounded-lg border-2 border-slate-400 px-3 text-sm bg-white">
                            <option value="">— Seleziona un soggetto per aggiungerlo —</option>
                            {contattiCond.map((c) => {
                              const key = c.id || c.soggetto_id;
                              const sub = [c.ruolo, c.unita].filter(Boolean).join(" · ");
                              return <option key={key} value={key}>{c.nome || c.ragione_sociale || "?"}{sub ? ` (${sub})` : ""}</option>;
                            })}
                          </select>
                          {(form.danneggianti || []).length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="danneggianti-chips">
                              {(form.danneggianti || []).map((d, i) => (
                                <span key={i} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full bg-orange-600 text-white text-sm font-semibold">
                                  <span className="max-w-[220px] truncate">{d.nome || `Danneggiante ${i + 1}`}</span>
                                  <button type="button" data-testid={`danneggiante-chip-remove-${i}`}
                                    onClick={() => removeDanneggiante(i)}
                                    className="hover:bg-red-700/40 rounded-full p-0.5 transition-colors" aria-label="Rimuovi">
                                    <X className="w-4 h-4" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      {(form.danneggianti || []).length === 0 && <div className="text-base text-slate-500">Nessun danneggiante indicato. Aggiungilo se ritieni ci siano soggetti terzi responsabili del sinistro.</div>}
                      <div className="space-y-4">
                        {(form.danneggianti || []).map((d, i) => (
                          <div key={i} data-testid={`danneggiante-${i}`} className="border-2 border-slate-200 rounded-lg p-3 bg-white">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-bold text-orange-800">Danneggiante {i + 1}</span>
                              <button type="button" data-testid={`remove-danneggiante-${i}`} onClick={() => removeDanneggiante(i)} className="text-red-600 hover:text-red-700"><X className="w-4 h-4" /></button>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-3">
                              <F label="Nome e cognome / Ragione sociale" v={d.nome} on={(v) => updateDanneggiante(i, "nome", v)} testid={`danneggiante-nome-${i}`} />
                              <F label="Telefono / contatti" v={d.contatti} on={(v) => updateDanneggiante(i, "contatti", v)} testid={`danneggiante-contatti-${i}`} />
                              <F label="Email" v={d.email || ""} on={(v) => updateDanneggiante(i, "email", v)} testid={`danneggiante-email-${i}`} type="email" />
                              <F label="Domicilio / Residenza" v={d.residenza || ""} on={(v) => updateDanneggiante(i, "residenza", v)} testid={`danneggiante-residenza-${i}`} />
                            </div>
                            <div className="mt-2">
                              <Label className="text-sm font-semibold">Descrizione responsabilità / dinamica</Label>
                              <Textarea data-testid={`danneggiante-descrizione-${i}`} value={d.descrizione || ""} onChange={(e) => updateDanneggiante(i, "descrizione", e.target.value)} className="mt-1" placeholder="Es. Proprietario appartamento sovrastante, causa perdita idrica non tempestivamente riparata" />
                            </div>
                            <div className="mt-2">
                              <Label className="text-sm font-semibold">Foto</Label>
                              <div className="mt-1"><PhotoUploader photos={d.foto || []} onChange={(v) => updateDanneggiante(i, "foto", v)} testid={`danneggiante-foto-${i}`} /></div>
                            </div>
                            <div className="mt-2">
                              <Label className="text-sm font-semibold">File / documenti</Label>
                              <div className="mt-1"><DocumentUploader docs={d.allegati || []} onChange={(v) => updateDanneggiante(i, "allegati", v)} testid={`danneggiante-doc-${i}`} /></div>
                            </div>
                            {/* Iter122k96 · Richiesta documentazione LEGATA al singolo danneggiante */}
                            <div className="mt-3 rounded-lg border-2 border-orange-200 bg-orange-50/40 p-3" data-testid={`danneggiante-docreq-${i}`}>
                              <div className="flex items-center justify-between gap-2 flex-wrap">
                                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-orange-800">
                                  <BellRing className="w-3.5 h-3.5" /> Documentazione al danneggiante
                                </div>
                                <button type="button" data-testid={`danneggiante-docreq-toggle-${i}`}
                                  onClick={() => {
                                    const nuovo = !d.doc_richiesta;
                                    updateDanneggiante(i, "doc_richiesta", nuovo);
                                    // Sync top-level: attivo se almeno un soggetto (danneggiato o danneggiante) lo è
                                    const anyDanneg = (form.danneggiati || []).some((x) => x.doc_richiesta);
                                    const anyDanng = nuovo || (form.danneggianti || []).some((x, k) => k !== i && x.doc_richiesta);
                                    set("documentazione_richiesta", anyDanneg || anyDanng);
                                  }}
                                  className={`px-2.5 py-1 rounded-full border-2 text-xs font-bold ${d.doc_richiesta ? "border-orange-600 bg-orange-600 text-white" : "border-slate-400 bg-white text-slate-600"}`}>
                                  {d.doc_richiesta ? "Richiesta attiva" : "Richiedi documentazione"}
                                </button>
                              </div>
                              {d.doc_richiesta && (
                                <div className="mt-2 grid sm:grid-cols-2 gap-2 items-end">
                                  <div>
                                    <Label className="text-xs font-semibold">Termine invio (opz.)</Label>
                                    <Input type="date" data-testid={`danneggiante-docreq-scad-${i}`}
                                      value={d.doc_scadenza || ""}
                                      onChange={(e) => {
                                        updateDanneggiante(i, "doc_scadenza", e.target.value);
                                        // Sync top-level scadenza = più vicina tra le attive (danneggiati + danneggianti)
                                        const scad_dan = (form.danneggiati || []).filter((x) => x.doc_richiesta && x.doc_scadenza).map((x) => x.doc_scadenza);
                                        const scad_dng = (form.danneggianti || []).map((x, k) => k === i ? e.target.value : x.doc_scadenza)
                                          .filter((v, k) => v && (form.danneggianti || [])[k]?.doc_richiesta);
                                        set("documentazione_scadenza", [...scad_dan, ...scad_dng].sort()[0] || "");
                                      }}
                                      className="mt-1 h-10 bg-white" />
                                  </div>
                                  {editId && (
                                    <Button type="button" data-testid={`danneggiante-docreq-sollecita-${i}`}
                                      variant="outline" onClick={sollecitaDoc}
                                      className="h-10 border-2 border-orange-500 text-orange-700 font-bold text-xs">
                                      <BellRing className="w-3.5 h-3.5 mr-1" /> Sollecita ora
                                    </Button>
                                  )}
                                </div>
                              )}
                              {!d.email && (
                                <p className="mt-2 text-[11px] text-orange-700">
                                  Suggerimento: inserisci l'<b>email</b> del danneggiante per ricevere il sollecito (senza email il sollecito non parte).
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}


                {openSoggetti.has("perito") && form.fase !== "Bozza-Denuncia" && (
                  <Section title="Perito">
                    <div className="sm:col-span-2">
                      <Label className="text-sm font-semibold">Seleziona perito (fornitori cat. Perito)</Label>
                      <select data-testid="sinistro-perito-select" value={form.perito_fornitore_id || ""} onChange={(e) => selectRuolo("perito", periti, e.target.value)}
                        className="h-11 mt-1 w-full border-2 border-slate-400 rounded-lg px-3 bg-white">
                        <option value="">— scegli o compila manualmente —</option>
                        {periti.map((a) => <option key={a.id} value={a.id}>{a.ragione_sociale}{a.email ? ` (${a.email})` : ""}</option>)}
                      </select>
                      {periti.length === 0 && (
                        <p className="text-xs text-slate-500 mt-1">Nessun perito in anagrafica. Aggiungine uno da &laquo;Account Fornitori&raquo; con categoria &laquo;Perito&raquo;.</p>
                      )}
                    </div>
                    <F label="Nome perito" v={form.perito_nome} on={(v) => set("perito_nome", v)} testid="sinistro-perito-nome" />
                    <F label="Contatti perito" v={form.perito_contatti} on={(v) => set("perito_contatti", v)} />
                    <F label="Email perito (per invio accordo)" v={form.perito_email} on={(v) => set("perito_email", v)} testid="sinistro-perito-email" placeholder="email@perito.it" />
                    {/* Iter122k40 · Invio scheda sinistro al perito: PDF denuncia + allegati pratica + BCC Studio */}
                    {editId && (form.perito_email || "").trim() && form.fase !== "Bozza-Denuncia" && (
                      <div className="sm:col-span-2 mt-1 flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border-2 border-amber-300 bg-amber-50/60 p-3">
                        <div className="flex-1">
                          <div className="text-sm font-bold text-amber-900">Invia scheda sinistro al perito</div>
                          <div className="text-xs text-amber-800/80 mt-0.5">
                            Trasmette a {form.perito_email} il PDF della denuncia + tutti gli allegati della pratica. Una copia BCC viene inviata allo Studio.
                            {form.scheda_perito_trasmessa_at && (
                              <span className="block mt-1 text-emerald-700 font-semibold">
                                Ultimo invio: {new Date(form.scheda_perito_trasmessa_at).toLocaleString("it-IT")}
                              </span>
                            )}
                          </div>
                        </div>
                        <Button type="button" data-testid="invia-scheda-perito-button"
                          onClick={inviaSchedaPerito} variant="outline"
                          className="h-10 border-2 border-amber-600 text-amber-800 font-bold hover:bg-amber-100">
                          <Send className="w-4 h-4 mr-1.5" /> Invia scheda sinistro
                        </Button>
                      </div>
                    )}
                  </Section>
                )}

                {openSoggetti.has("liquidatore") && form.fase !== "Bozza-Denuncia" && (
                  <Section title="Liquidatore">
                    <div className="sm:col-span-2">
                      <Label className="text-sm font-semibold">Seleziona liquidatore (fornitori cat. Liquidatore)</Label>
                      <select data-testid="sinistro-liquidatore-select" value={form.liquidatore_fornitore_id || ""} onChange={(e) => selectRuolo("liquidatore", liquidatori, e.target.value)}
                        className="h-11 mt-1 w-full border-2 border-slate-400 rounded-lg px-3 bg-white">
                        <option value="">— scegli o compila manualmente —</option>
                        {liquidatori.map((a) => <option key={a.id} value={a.id}>{a.ragione_sociale}{a.email ? ` (${a.email})` : ""}</option>)}
                      </select>
                      {liquidatori.length === 0 && (
                        <p className="text-xs text-slate-500 mt-1">Nessun liquidatore in anagrafica. Aggiungine uno da &laquo;Account Fornitori&raquo; con categoria &laquo;Liquidatore&raquo;.</p>
                      )}
                    </div>
                    <F label="Nome liquidatore" v={form.liquidatore_nome} on={(v) => set("liquidatore_nome", v)} testid="sinistro-liquidatore-nome" />
                    <F label="Contatti liquidatore" v={form.liquidatore_contatti} on={(v) => set("liquidatore_contatti", v)} />
                    <F label="Email liquidatore (per invio accordo)" v={form.liquidatore_email} on={(v) => set("liquidatore_email", v)} testid="sinistro-liquidatore-email" placeholder="email@liquidatore.it" />
                  </Section>
                )}
              </div>
            )}

            {/* PANE: GESTIONE */}
            {mainTab === "gestione" && (
              <div className="space-y-3" data-testid="pane-gestione">
                <SubTabs testidPrefix="gestione"
                  tabs={[
                    { key: "documenti", label: "Documenti", icon: Folder },
                    // Iter122k93 · sub-tab UNIFICATO Valutazione + Liquidazione + Atto liquidazione
                    ...(form.fase !== "Bozza-Denuncia" && form.fase !== "Aperto-Istruttoria" ? [
                      { key: "valutazione", label: "Valutazione · Liquidazione", icon: Euro },
                    ] : []),
                    // Chat visibile da Istruttoria in poi
                    ...(form.fase !== "Bozza-Denuncia" ? [
                      { key: "chat", label: "Chat", icon: MessageCircle },
                    ] : []),
                    { key: "cronologia", label: "Cronologia", icon: Clock },
                    ...(form.fase !== "Bozza-Denuncia" && form.fase !== "Aperto-Istruttoria" ? [
                      { key: "accordi", label: "Accordi", icon: FileText },
                    ] : []),
                  ]}
                  openSet={openGestione} onToggle={toggleGestione}
                />

                {openGestione.has("documenti") && (
                  <div className="space-y-3">
                    {/* Iter122h4 · Documenti dagli interventi urgenti sulla manutenzione collegata */}
                    {form.interventi_urgenti && form.ticket_id && (
                      <InterventiDocumentiPanel sinistroId={editId}
                        eseguiti={form.interventi_documenti_esclusi_ids || []}
                        onExclusiChange={(v) => set("interventi_documenti_esclusi_ids", v)} />
                    )}
                    <div className="border-2 border-slate-200 rounded-lg p-4 space-y-3">
                      <div className="text-sm font-bold uppercase tracking-wide text-slate-600 flex items-center gap-2">
                        <Folder className="w-4 h-4" /> Documenti generali della pratica
                      </div>
                      <p className="text-xs text-slate-500">
                        Documenti/foto trasversali al sinistro (non legati a un singolo danneggiato):
                        preventivi tecnici, verbali, corrispondenza generica, foto d&apos;ambiente, ecc.
                      </p>
                      <DocumentUploader docs={form.documenti_pratica || []} onChange={(v) => set("documenti_pratica", v)} testid="sinistro-documenti-pratica" />
                    </div>
                  </div>
                )}

                {openGestione.has("valutazione") && form.fase !== "Bozza-Denuncia" && form.fase !== "Aperto-Istruttoria" && (
                  <div className="space-y-3">
                    <Section title="Dati pratica">
                      <F label="Numero sinistro" v={form.numero_sinistro} on={(v) => set("numero_sinistro", v)} testid="sinistro-numero" />
                      <F label="Data apertura" v={form.data_apertura} on={(v) => set("data_apertura", v)} placeholder="gg/mm/aaaa" testid="sinistro-data-apertura" />
                      <F label="Importo richiesto" v={form.importo_richiesto} on={(v) => set("importo_richiesto", v)} placeholder="€" />
                      {/* Iter122k93 · rimosso "Importo liquidato" duplicato (usiamo `liquidazione_offerta` in "Offerta compagnia") */}
                    </Section>

                    <div className="border-2 border-slate-200 rounded-lg p-4 space-y-3">
                      <div className="text-sm font-bold uppercase tracking-wide text-slate-600">Esito valutazione</div>
                      <div className="grid sm:grid-cols-3 gap-2">
                        {[
                          // Iter122k76 · Colori richiesti per esito selezionato
                          { v: "", label: "Non determinato", active: "border-sky-500 bg-sky-100 text-sky-900" },
                          { v: "indennizzabile", label: "Indennizzabile", active: "border-green-500 bg-green-100 text-green-900" },
                          { v: "non_indennizzabile", label: "Non indennizzabile", active: "border-red-500 bg-red-100 text-red-900" },
                        ].map((opt) => (
                          <button key={opt.v || "none"} type="button" data-testid={`esito-${opt.v || "none"}`}
                            onClick={() => set("esito_valutazione", opt.v)}
                            className={`h-11 rounded-lg border-2 font-bold text-sm ${form.esito_valutazione === opt.v ? opt.active : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      {form.esito_valutazione && (
                        <div>
                          <Label className="text-sm font-semibold">Motivazione della compagnia/perito</Label>
                          <Textarea data-testid="esito-motivazione" value={form.esito_motivazione} onChange={(e) => set("esito_motivazione", e.target.value)} className="mt-1"
                            placeholder={form.esito_valutazione === "non_indennizzabile" ? "Es. Danno escluso dalla polizza (art. …)" : "Es. Rientra nella copertura per allagamento accidentale"} />
                        </div>
                      )}
                      {form.esito_valutazione === "non_indennizzabile" && (
                        <div>
                          <Label className="text-sm font-semibold">Controdeduzioni Studio</Label>
                          <Textarea data-testid="esito-controdeduzioni" value={form.controdeduzioni_studio} onChange={(e) => set("controdeduzioni_studio", e.target.value)} className="mt-1"
                            placeholder="Replica dello Studio ai motivi di esclusione (opzionale)" />
                        </div>
                      )}
                    </div>

                    {/* Iter122k147 · Le sezioni "Ricezione pagamento" e "Liquidazione" hanno senso
                        SOLO se il sinistro è stato dichiarato indennizzabile. Se non indennizzabile
                        mostriamo un banner informativo; se non determinato le sezioni restano nascoste
                        finché lo Studio non registra l'esito. */}
                    {form.esito_valutazione === "non_indennizzabile" && (
                      <div className="border-2 border-red-200 bg-red-50 rounded-lg p-4 text-sm text-red-900"
                           data-testid="sinistro-non-indennizzabile-banner">
                        <b>Sinistro non indennizzabile.</b> Non sono previste sezioni relative ad
                        offerta della compagnia, ricezione pagamento e riparto tra danneggiati.
                        Se in seguito la compagnia riconsiderasse la posizione (a seguito delle
                        controdeduzioni), imposta l'esito su <i>Indennizzabile</i> per riabilitarle.
                      </div>
                    )}

                    {form.esito_valutazione === "indennizzabile" && (
                      <>
                    <div className="border-2 border-emerald-200 rounded-lg p-4 bg-emerald-50/40">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-bold uppercase tracking-wide text-emerald-800 flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4" /> Ricezione pagamento
                        </div>
                        <button type="button" data-testid="pagamento-confermato-toggle"
                          onClick={() => set("pagamento_confermato", !form.pagamento_confermato)}
                          className={`px-3 py-1.5 rounded-full border-2 text-sm font-bold ${form.pagamento_confermato ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-400 bg-white text-slate-600"}`}>
                          {form.pagamento_confermato ? "Confermata" : "Non ancora ricevuto"}
                        </button>
                      </div>
                      <p className="text-xs text-emerald-900 mt-2">
                        Il danneggiato/condominio conferma la ricezione del bonifico dalla compagnia: necessario per poter chiudere la pratica.
                      </p>
                    </div>

                    {/* Iter122k93 · Contenuto "Liquidazione" integrato nel sub-tab Valutazione */}
                    {(() => {
                      const offerta = parseEuro(form.liquidazione_offerta);
                      const franchigia = parseEuro(form.liquidazione_franchigia);
                      const netto = Math.round((offerta - franchigia) * 100) / 100;
                      const totale = Math.round((form.liquidazione_riparto || []).reduce((acc, r) => acc + parseEuro(r.importo), 0) * 100) / 100;
                      const quadra = netto > 0 && Math.abs(totale - netto) <= 0.01;
                      return (
                        <div className="space-y-3" data-testid="pane-liquidazione">
                          <Section title="Offerta compagnia (importo liquidato)">
                            <F label="Importo offerto (€)" v={form.liquidazione_offerta} on={(v) => set("liquidazione_offerta", v)} placeholder="es. 5.000,00" testid="liq-offerta" />
                            <F label="Franchigia / scoperto (€)" v={form.liquidazione_franchigia} on={(v) => set("liquidazione_franchigia", v)} placeholder="es. 500,00" testid="liq-franchigia" />
                          </Section>

                          <div className="border-2 border-purple-200 rounded-lg p-4 bg-purple-50/30 space-y-3">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <div className="text-sm font-bold uppercase tracking-wide text-purple-800 flex items-center gap-2">
                                <Euro className="w-4 h-4" /> Riparto tra i beneficiari
                              </div>
                              <div className="flex gap-2 flex-wrap">
                                {(form.liquidazione_riparto || []).length === 0 && (form.danneggiati || []).some((d) => d.nome) && (
                                  <Button type="button" data-testid="liq-prefill-danneggiati" variant="outline" size="sm" className="h-9" onClick={prefillRipartoDanneggiati}>
                                    <Users className="w-4 h-4 mr-1" /> Precompila dai danneggiati
                                  </Button>
                                )}
                                <Button type="button" data-testid="liq-add-row" variant="outline" size="sm" className="h-9" onClick={addRipartoRow}>
                                  <Plus className="w-4 h-4 mr-1" /> Aggiungi riga
                                </Button>
                              </div>
                            </div>
                            {(form.liquidazione_riparto || []).length === 0 && (
                              <div className="text-sm text-slate-500">Nessuna riga di riparto. Precompila dai danneggiati o aggiungi manualmente.</div>
                            )}
                            <div className="space-y-2">
                              {(form.liquidazione_riparto || []).map((r, i) => (
                                <div key={i} data-testid={`liq-riparto-row-${i}`} className="grid grid-cols-1 sm:grid-cols-[1fr_130px_1fr_auto] gap-2 items-center bg-white border-2 border-slate-200 rounded-lg p-2">
                                  <Input data-testid={`liq-riparto-nome-${i}`} value={r.nome || ""} onChange={(e) => updateRiparto(i, "nome", e.target.value)} placeholder="Beneficiario" className="h-10" />
                                  <Input data-testid={`liq-riparto-importo-${i}`} value={r.importo || ""} onChange={(e) => updateRiparto(i, "importo", e.target.value)} placeholder="€" className="h-10" />
                                  <Input data-testid={`liq-riparto-note-${i}`} value={r.note || ""} onChange={(e) => updateRiparto(i, "note", e.target.value)} placeholder="Note (opz.)" className="h-10" />
                                  <button type="button" data-testid={`liq-riparto-remove-${i}`} onClick={() => removeRiparto(i)} className="text-red-600 hover:text-red-700 justify-self-end sm:justify-self-auto"><X className="w-4 h-4" /></button>
                                </div>
                              ))}
                            </div>
                            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 pt-2 border-t border-purple-200 text-sm">
                              <span data-testid="liq-netto">Netto da ripartire: <b>{fmtEuro(netto)}</b></span>
                              <span>Totale ripartito: <b>{fmtEuro(totale)}</b></span>
                              {(form.liquidazione_riparto || []).length > 0 && netto > 0 && (
                                <span data-testid="liq-totale-badge" className={`inline-flex items-center gap-1 text-xs font-bold rounded-full px-2.5 py-1 ${quadra ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                                  {quadra ? <><CheckCircle2 className="w-3.5 h-3.5" /> Il riparto quadra</> : <><AlertCircle className="w-3.5 h-3.5" /> Differenza: {fmtEuro(Math.round((netto - totale) * 100) / 100)}</>}
                                </span>
                              )}
                            </div>
                          </div>

                          <div>
                            <Label className="text-sm font-semibold">Note sulla liquidazione</Label>
                            <Textarea data-testid="liq-note" value={form.liquidazione_note} onChange={(e) => set("liquidazione_note", e.target.value)} className="mt-1"
                              placeholder="Es. accordi con il liquidatore, tempi di bonifico, coordinate comunicate..." />
                          </div>

                          {/* Iter122k95 · Atto di Liquidazione: link all'editor "/conferma-preventivo" (rifatto) */}
                          <AttoLiquidazioneCard sinistroId={editId} form={form} />
                        </div>
                      );
                    })()}
                      </>
                    )}
                  </div>
                )}

                {openGestione.has("chat") && form.fase !== "Bozza-Denuncia" && (
                  editId ? (
                    <div className="space-y-3">
                      <SinistroChat sinistroId={editId} messaggi={form.messaggi || []} me="Studio" kind="condomino"
                        title="Chat Studio ↔ Condòmino/Danneggiato"
                        onNew={(m) => setForm((f) => ({ ...f, messaggi: [...(f.messaggi || []), m] }))} />
                      <SinistroChat sinistroId={editId} messaggi={form.messaggi || []} me="Studio" kind="assicurazione"
                        title="Chat Studio ↔ Compagnia/Agenzia"
                        onNew={(m) => setForm((f) => ({ ...f, messaggi: [...(f.messaggi || []), m] }))} />
                    </div>
                  ) : (
                    <div className="text-sm text-slate-500 border-2 border-dashed border-slate-200 rounded-lg p-4">Salva prima la pratica per abilitare la chat con il condòmino.</div>
                  )
                )}

                {openGestione.has("cronologia") && (
                  <div className="border-2 border-slate-200 rounded-lg p-4" data-testid="sinistro-cronologia">
                    <div className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1"><Clock className="w-4 h-4" /> Cronologia</div>
                    {(form.history || []).length === 0 ? (
                      <div className="text-sm text-slate-400">Nessuna voce di cronologia.</div>
                    ) : (
                      <div className="space-y-1 max-h-96 overflow-y-auto">
                        {[...(form.history || [])].reverse().map((h, i) => {
                          const when = h.at ? new Date(h.at) : null;
                          const whenStr = when && !isNaN(when.getTime())
                            ? when.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
                            : "";
                          return (
                            <div key={h.id || i} data-testid={`cronologia-row-${i}`} className="text-sm text-slate-700 border-b border-slate-100 py-2">
                              <div className="flex items-start gap-2">
                                <span className="text-brand">•</span>
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium">{h.action}</div>
                                  {h.note ? <div className="text-xs text-slate-600 mt-1 whitespace-pre-wrap bg-slate-50 border border-slate-200 rounded px-2 py-1.5 font-mono leading-snug" data-testid={`cronologia-note-${i}`}>{h.note}</div> : null}
                                  <div className="text-xs text-slate-400 mt-0.5">
                                    {whenStr}{h.by ? ` · ${h.by}` : ""}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {openGestione.has("accordi") && form.fase !== "Bozza-Denuncia" && form.fase !== "Aperto-Istruttoria" && (
                  <div className="border-2 border-slate-200 rounded-lg p-4" data-testid="sinistro-accordi-storico">
                    <div className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1"><FileText className="w-4 h-4" /> Accordi di liquidazione</div>
                    {accordi.length === 0 ? (
                      <div className="text-sm text-slate-400">Nessun accordo. Genera un accordo di liquidazione tramite il pulsante &laquo;Sottoscrivi accordo&raquo; in fondo al dialog.</div>
                    ) : (
                      <div className="space-y-2">
                        {accordi.map((a) => (
                          <div key={a.id} data-testid={`accordo-row-${a.id}`} className="flex flex-wrap items-center gap-2 justify-between border rounded-lg px-3 py-2 bg-slate-50">
                            <div className="min-w-0">
                              <div className="font-semibold text-slate-800 text-sm">{a.numero} · <span className={`text-xs ${a.stato === "inviata" ? "text-green-700" : "text-slate-500"}`}>{a.stato === "inviata" ? "Inviato" : a.stato === "generata" ? "Generato" : "Bozza"}</span></div>
                              <div className="text-xs text-slate-500 truncate">
                                {a.inviata_at ? `Inviato il ${(a.inviata_at || "").slice(0, 10)}` : "Non ancora inviato"}
                                {a.inviata_a ? ` → ${a.inviata_a}` : ""}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              {a.signed_file_id && (
                                <button type="button" onClick={() => openSecureFile(`/files/${a.signed_file_id}`)} data-testid={`accordo-download-${a.id}`}
                                  className="inline-flex items-center gap-1 text-xs font-semibold text-brand border border-brand/40 rounded px-2 py-1 hover:bg-brand/5">
                                  <Download className="w-3.5 h-3.5" /> PDF
                                </button>
                              )}
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" data-testid={`accordo-open-${a.id}`}
                                onClick={() => navigate(`/conferma-preventivo/${a.id}`)}>Apri</Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div>
              <Label className="text-sm font-bold">Note</Label>
              <Textarea data-testid="sinistro-note" value={form.note} onChange={(e) => set("note", e.target.value)} className="mt-1" />
            </div>

            {error && <div className="p-3 bg-red-50 text-red-800 rounded-lg text-base font-semibold">{error}</div>}
          </div>
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} className="h-12">Annulla</Button>
            {editId && (
              <Button data-testid="download-denuncia-pdf-button" onClick={downloadDenuncia} variant="outline" className="h-12">
                <Download className="w-4 h-4 mr-1.5" /> PDF denuncia
              </Button>
            )}
            {editId && form.fase !== "Bozza-Denuncia" && form.fase !== "Aperto-Istruttoria" && (
              <Button data-testid="report-finale-pdf-button" onClick={downloadReport} variant="outline" className="h-12 border-2 border-purple-400 text-purple-700 font-bold">
                <FileText className="w-4 h-4 mr-1.5" /> Report finale
              </Button>
            )}
            {editId && form.fase === "Bozza-Denuncia" && (form.compagnia_email || form.agenzia_email) && (
              <Button data-testid="trasmetti-denuncia-button" onClick={trasmettiDenuncia} variant="outline"
                className="h-12 border-2 border-amber-500 text-amber-700 font-bold">
                <Send className="w-4 h-4 mr-1.5" /> Trasmetti denuncia
              </Button>
            )}
            {/* Iter122k81 · Invia integrazione (soggetti ritardatari, foto/documenti aggiunti)
                al Perito/Agenzia — visibile solo dopo la trasmissione della denuncia */}
            {editId && form.denuncia_trasmessa_at && form.fase === "Aperto-Istruttoria" && (
              <Button data-testid="invia-integrazione-button" onClick={inviaIntegrazione} variant="outline"
                className="h-12 border-2 border-indigo-500 text-indigo-700 font-bold">
                <Send className="w-4 h-4 mr-1.5" /> Invia integrazione
              </Button>
            )}
            {editId && form.fase !== "Bozza-Denuncia" && form.fase !== "Aperto-Istruttoria" && (
              <Button data-testid="sottoscrivi-accordo-button" onClick={() => navigate(`/conferma-preventivo?contesto=sinistro&sinistro_id=${editId}&condominio_id=${form.condominio_id}`)}
                variant="outline" className="h-12 border-2 border-brand text-brand font-bold">
                <FileText className="w-4 h-4 mr-1.5" /> Sottoscrivi accordo
              </Button>
            )}
            <Button data-testid="save-sinistro-button" onClick={save} disabled={!form.condominio_id} className="h-12 bg-brand hover:bg-brand-dark">
              {readOnlyMode ? "Salva modifiche compagnia" : "Salva pratica"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border-2 border-slate-200 rounded-lg p-4">
      <div className="text-sm font-bold uppercase tracking-wide text-slate-600 mb-3">{title}</div>
      <div className="grid sm:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function F({ label, v, on, placeholder, testid, type, required }) {
  return (
    <div>
      <Label className="text-sm font-semibold">
        {label}{required ? <span className="text-red-600 ml-0.5" title="Campo obbligatorio per uscire dalla Bozza">*</span> : null}
      </Label>
      <Input data-testid={testid} type={type || "text"} value={v} onChange={(e) => on(e.target.value)} className="h-11 mt-1 bg-white" placeholder={placeholder} />
    </div>
  );
}

// Iter122fp12 · helper condiviso: elenco campi mancanti per far uscire la pratica dalla Bozza.
// Lo usa sia il pre-check dell'invio denuncia sia il pulsante di avanzamento fase.
function campiObbligatoriMancanti(f) {
  const missing = [];
  if (!(f.condominio_id || "").trim()) missing.push("Condominio");
  if (!(f.amministratore_legale || "").trim()) missing.push("Amministratore legale rapp.te");
  if (!(f.data_sinistro || "").trim()) missing.push("Data sinistro");
  if (!(f.oggetto || "").trim()) missing.push("Oggetto");
  if (!(f.descrizione || "").trim()) missing.push("Descrizione");
  if (!(f.compagnia_nome || "").trim()) missing.push("Compagnia assicurativa");
  if (!(f.agenzia_nome || "").trim()) missing.push("Agenzia");
  const dann = (f.danneggiati || []).filter((d) => (d.nome || "").trim());
  if (dann.length === 0) missing.push("Almeno un Danneggiato con nome");
  return missing;
}

// Iter122cs · Stepper 4-fasi (Bozza → Istruttoria → Valutazione/Liquidazione → Chiuso).
// Iter122k76 · Palette semaforo richiesta dall'utente:
//   • fase corrente = giallo (in corso), fase completata = verde (emerald), fase futura = grigio.
// Bozza-Denuncia mantiene grigio come stato corrente (è la fase iniziale prima
// della trasmissione della denuncia).
const FASE_BTN_STYLE = {
  "Bozza-Denuncia": {
    current: "border-slate-500 bg-slate-100 text-slate-800",
    done:    "border-emerald-500 bg-emerald-50 text-emerald-800",
    future:  "border-slate-200 bg-white text-slate-500 hover:border-slate-500 hover:bg-slate-50",
  },
  "Aperto-Istruttoria": {
    current: "border-yellow-500 bg-yellow-100 text-yellow-900",
    done:    "border-emerald-500 bg-emerald-50 text-emerald-800",
    future:  "border-slate-200 bg-white text-slate-500 hover:border-yellow-500 hover:bg-yellow-50",
  },
  "Aperto-Valutazione": {
    current: "border-yellow-500 bg-yellow-100 text-yellow-900",
    done:    "border-emerald-500 bg-emerald-50 text-emerald-800",
    future:  "border-slate-200 bg-white text-slate-500 hover:border-yellow-500 hover:bg-yellow-50",
  },
  "Chiuso-Liquidato": {
    current: "border-emerald-600 bg-emerald-100 text-emerald-900",
    done:    "border-emerald-500 bg-emerald-50 text-emerald-800",
    future:  "border-slate-200 bg-white text-slate-500 hover:border-emerald-600 hover:bg-emerald-50",
  },
};
function FaseStepper({ fase, onAvanza, editable, esito, pagamentoConfermato }) {
  // Iter122k76 · normalizza legacy Aperto-Liquidazione → Aperto-Valutazione (merged)
  const faseView = phaseForDisplay(fase);
  const idx = Math.max(0, FASI.indexOf(faseView));
  // Iter122k148 · Badge di stato aggiuntivi accanto alle fasi Valutazione e Chiuso:
  //   • "ATTESA PAGAMENTO" — sinistro indennizzabile ma pagamento non ancora ricevuto
  //     e pratica non chiusa (chip giallo sopra il pulsante Valutazione/Liquidazione).
  //   • "NON INDENNIZZABILE" — sinistro chiuso con esito non_indennizzabile
  //     (chip rosso sopra il pulsante Chiuso).
  const showAttesa = esito === "indennizzabile" && !pagamentoConfermato && fase !== "Chiuso-Liquidato";
  const showNonInd = esito === "non_indennizzabile" && fase === "Chiuso-Liquidato";
  // Iter122k150 · Stato terminale positivo: sinistro chiuso con esito indennizzabile
  // e pagamento ricevuto → badge verde "PAGATO · CHIUSO" sopra la fase 4.
  const showPagatoChiuso = esito === "indennizzabile" && !!pagamentoConfermato && fase === "Chiuso-Liquidato";
  return (
    <div className="border-2 border-slate-400 rounded-lg p-3 bg-white" data-testid="fase-stepper">
      <div className="text-xs font-bold uppercase tracking-wide text-slate-700 mb-2 flex items-center gap-1">
        <ShieldAlert className="w-3.5 h-3.5" /> Avanzamento pratica
      </div>
      <div className="flex flex-wrap gap-1.5">
        {FASI.map((f, i) => {
          const done = i < idx;
          const current = i === idx;
          const clickable = editable;
          const style = FASE_BTN_STYLE[f] || FASE_BTN_STYLE["Bozza-Denuncia"];
          const base = "flex-1 min-w-[120px] min-h-[52px] rounded-lg border-2 font-bold px-2 py-1 flex flex-col items-center justify-center transition";
          const cls = current ? style.current : done ? style.done : style.future;
          const badge = f === "Aperto-Valutazione" && showAttesa
            ? { label: "ATTESA PAGAMENTO", cls: "bg-amber-100 text-amber-800 border-amber-400", testid: "badge-attesa-pagamento" }
            : f === "Chiuso-Liquidato" && showNonInd
            ? { label: "NON INDENNIZZABILE", cls: "bg-red-100 text-red-800 border-red-400", testid: "badge-non-indennizzabile" }
            : f === "Chiuso-Liquidato" && showPagatoChiuso
            ? { label: "PAGATO · CHIUSO", cls: "bg-emerald-100 text-emerald-800 border-emerald-400", testid: "badge-pagato-chiuso" }
            : null;
          return (
            <div key={f} className="flex-1 min-w-[120px] flex flex-col gap-1">
              {badge && (
                <span data-testid={badge.testid}
                  className={`self-center text-[10px] leading-tight font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border ${badge.cls}`}>
                  {badge.label}
                </span>
              )}
              <button type="button" data-testid={`fase-step-${f}`}
                disabled={!clickable || current}
                onClick={() => clickable && !current && onAvanza(f)}
                className={`${base} ${cls} ${(!clickable || current) ? "opacity-100 cursor-default" : ""}`}>
                <span className="flex flex-col items-center leading-tight text-center w-full">
                  <span className="flex items-center gap-1">
                    {done && <CheckCircle2 className="w-3.5 h-3.5" />}
                    <span>{i + 1}.</span>
                  </span>
                  <span className="text-[11px] sm:text-xs break-words">
                    {/* Iter122h7 · Il tag "Bozza" diventa "Denuncia" una volta trasmessa */}
                    {f === "Bozza-Denuncia" && done ? "Denuncia" : FASE_LABEL[f]}
                  </span>
                </span>
              </button>
            </div>
          );
        })}
      </div>
      {!editable && (
        <p className="text-xs text-slate-500 mt-2">Salva prima la pratica per abilitare i passaggi di fase.</p>
      )}
    </div>
  );
}

// Iter122h2 · Sotto-categorie a scomparsa (accordion) — normalmente chiuse.
// Ogni voce è un pulsante-header cliccabile; il body viene renderizzato inline dal caller.
function SubTabs({ tabs, testidPrefix, openSet, onToggle }) {
  return (
    <div className="flex flex-col gap-1.5" data-testid={`${testidPrefix}-subtabs`}>
      {tabs.map((t) => {
        const Icon = t.icon;
        const open = openSet && openSet.has(t.key);
        return (
          <button key={t.key} type="button" data-testid={`${testidPrefix}-tab-${t.key}`}
            aria-expanded={!!open}
            onClick={() => onToggle && onToggle(t.key)}
            className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 font-semibold text-sm transition-colors ${open ? "bg-slate-100 border-slate-500 text-slate-800" : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50 hover:border-slate-400"}`}>
            {Icon && <Icon className="w-4 h-4 shrink-0" />}
            <span className="flex-1 text-left">{t.label}</span>
            <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        );
      })}
    </div>
  );
}


function CondInsuranceRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex gap-2 text-sm min-w-0">
      <span className="text-slate-500 shrink-0 w-24 sm:w-28">{label}</span>
      <span className="font-medium text-slate-800 break-all min-w-0 flex-1">{value}</span>
    </div>
  );
}

function CondInsuranceInfo({ c }) {
  const has = c.compagnia_nome || c.agenzia_nome || c.numero_polizza;
  if (!has) {
    return (
      <div data-testid="cond-insurance-empty" className="text-sm text-slate-500 border-2 border-dashed border-slate-200 rounded-lg p-3">
        Questo condominio non ha ancora dati assicurativi. Inseriscili in <b>Stabili → Modifica → Dati assicurativi</b> per richiamarli in automatico.
      </div>
    );
  }
  return (
    <div data-testid="cond-insurance-info" className="border-2 border-amber-200 rounded-lg p-3 bg-amber-50/50 space-y-2">
      <div className="text-sm font-bold uppercase tracking-wide text-amber-800 flex items-center gap-2"><Umbrella className="w-4 h-4" /> Dati assicurativi del condominio</div>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
        <div className="space-y-1">
          <div className="font-bold text-brand">Compagnia</div>
          <CondInsuranceRow label="Nome" value={c.compagnia_nome} />
          <CondInsuranceRow label="Telefono" value={c.compagnia_contatti} />
          <CondInsuranceRow label="Email" value={c.compagnia_email} />
          <CondInsuranceRow label="PEC" value={c.compagnia_pec} />
          <CondInsuranceRow label="Indirizzo" value={c.compagnia_indirizzo} />
        </div>
        <div className="space-y-1">
          <div className="font-bold text-brand">Agenzia</div>
          <CondInsuranceRow label="Nome" value={c.agenzia_nome} />
          <CondInsuranceRow label="Telefono" value={c.agenzia_contatti} />
          <CondInsuranceRow label="Email" value={c.agenzia_email} />
          <CondInsuranceRow label="PEC" value={c.agenzia_pec} />
          <CondInsuranceRow label="Indirizzo" value={c.agenzia_indirizzo} />
        </div>
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-1 pt-1 border-t border-amber-200">
        <CondInsuranceRow label="N° polizza" value={c.numero_polizza} />
        <CondInsuranceRow label="Decorrenza" value={c.polizza_decorrenza} />
        <CondInsuranceRow label="Scadenza" value={c.polizza_scadenza} />
      </div>
    </div>
  );
}

// Iter122h4 · Pannello documenti trasferiti dagli interventi urgenti (manutenzione collegata).
// Read-only sui file: apre in nuova scheda tramite download token; il tasto "elimina" li
// nasconde solo dalla scheda sinistro (esclusi), senza cancellarli dalla manutenzione.
function InterventiDocumentiPanel({ sinistroId, eseguiti, onExclusiChange }) {
  const [docs, setDocs] = useState([]);
  const [ticketNumero, setTicketNumero] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const reload = useCallback(async () => {
    if (!sinistroId) return;
    setLoading(true); setError("");
    try {
      const { data } = await api.get(`/sinistri/${sinistroId}/interventi-documenti`);
      setDocs(data.documenti || []);
      setTicketNumero(data.ticket_numero || "");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Errore nel caricamento dei documenti");
    } finally { setLoading(false); }
  }, [sinistroId]);

  useEffect(() => { reload(); }, [reload]);

  const escludi = async (fid) => {
    try {
      const { data } = await api.post(`/sinistri/${sinistroId}/interventi-documenti/${fid}/escludi`);
      onExclusiChange && onExclusiChange(data.interventi_documenti_esclusi_ids || []);
      reload();
      toast.success("Documento rimosso dalla scheda sinistro (resta sulla manutenzione)");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  const ripristinaTutti = async () => {
    if (!(eseguiti || []).length) return;
    try {
      // Ripristina uno alla volta (la lista è tipicamente breve)
      for (const fid of (eseguiti || [])) {
        // eslint-disable-next-line no-await-in-loop
        await api.post(`/sinistri/${sinistroId}/interventi-documenti/${fid}/ripristina`);
      }
      onExclusiChange && onExclusiChange([]);
      reload();
      toast.success("Documenti ripristinati");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  return (
    <div className="border-2 border-amber-200 rounded-lg p-4 space-y-3 bg-amber-50/30" data-testid="interventi-documenti-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-bold uppercase tracking-wide text-amber-900 flex items-center gap-2">
          <Wrench className="w-4 h-4" /> Documenti dagli interventi urgenti
          {ticketNumero && <span className="text-xs font-normal text-amber-800">· Manutenzione {ticketNumero}</span>}
        </div>
        {(eseguiti || []).length > 0 && (
          <Button type="button" size="sm" variant="outline" onClick={ripristinaTutti}
            data-testid="interventi-doc-ripristina-tutti"
            className="h-8 text-xs gap-1 border-amber-400 text-amber-900">
            <RotateCcw className="w-3.5 h-3.5" /> Ripristina rimossi ({(eseguiti || []).length})
          </Button>
        )}
      </div>
      <p className="text-xs text-amber-900/80">
        Foto e allegati caricati sulla manutenzione collegata (sia in fase di segnalazione sia di esecuzione).
        Apri per visualizzarli o rimuovili dalla scheda sinistro: la rimozione è locale — i file restano
        sempre disponibili sulla manutenzione originale.
      </p>
      {loading && <div className="text-sm text-slate-600" data-testid="interventi-doc-loading">Caricamento…</div>}
      {error && <div className="text-sm text-red-700 font-semibold">{error}</div>}
      {!loading && docs.length === 0 && !error && (
        <div className="text-sm text-slate-500 italic" data-testid="interventi-doc-empty">
          Nessun documento presente sulla manutenzione collegata
          {(eseguiti || []).length > 0 && " (tutti rimossi — usa Ripristina per rimostrarli)"}.
        </div>
      )}
      {!loading && docs.length > 0 && (
        <ul className="flex flex-col gap-2" data-testid="interventi-doc-list">
          {docs.map((d) => (
            <li key={d.id}
              className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-2 rounded-lg border-2 border-amber-200 bg-white"
              data-testid={`interventi-doc-item-${d.id}`}>
              {d.kind === "foto"
                ? <Camera className="w-5 h-5 text-amber-700 shrink-0" />
                : <FileText className="w-5 h-5 text-amber-700 shrink-0" />}
              {/* Su mobile occupa tutta la larghezza; su sm+ affianca i bottoni con flex-1 */}
              <div className="min-w-0 basis-[calc(100%-1.75rem)] sm:basis-0 sm:flex-1">
                <div className="break-words text-sm font-semibold text-slate-800">{d.filename}</div>
                <div className="text-xs text-slate-500">
                  {d.source === "esecuzione" ? "Fase esecuzione" : "Fase segnalazione"} · {d.kind === "foto" ? "Foto" : "Allegato"}
                </div>
              </div>
              <div className="flex items-center gap-1 ml-auto sm:ml-0 shrink-0">
                <button type="button" data-testid={`interventi-doc-open-${d.id}`}
                  onClick={() => openSecureFile(`/files/${d.id}`)}
                  className="h-8 px-2 rounded-md text-amber-800 hover:bg-amber-100 flex items-center gap-1 text-xs font-semibold"
                  title="Apri il file in una nuova scheda">
                  <ExternalLink className="w-4 h-4" /> Apri
                </button>
                <button type="button" data-testid={`interventi-doc-remove-${d.id}`}
                  onClick={() => escludi(d.id)}
                  className="h-8 w-8 rounded-md text-red-600 hover:bg-red-50 flex items-center justify-center"
                  title="Rimuovi dalla scheda sinistro (non elimina dalla manutenzione)">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

